import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Typography,
  Tabs,
  Tab,
  Button,
  Paper,
  Skeleton,
  Grid,
  MenuItem,
  DialogContent,
  Dialog,
  DialogActions,
  Avatar,
  CircularProgress,
  Chip,
  Divider,
  Stack,
  IconButton,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import BoltIcon from '@mui/icons-material/Bolt';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import PsychologyAltRoundedIcon from '@mui/icons-material/PsychologyAltRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import StreamRoundedIcon from '@mui/icons-material/StreamRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CallMadeIcon from '@mui/icons-material/CallMade';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DialogTitle from '@mui/material/DialogTitle';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import EmailIcon from '@mui/icons-material/Email';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SendIcon from '@mui/icons-material/Send';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { toast } from 'react-toastify';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import apiClient from '../../services/apiClient';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import {
  MRT_FullScreenToggleButton as MrtFullScreenToggleButton,
  MRT_GlobalFilterTextField as MrtGlobalFilterTextField,
  MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MrtToggleFiltersButton,
  MRT_ToggleGlobalFilterButton as MrtToggleGlobalFilterButton,
} from 'material-react-table';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useAIAssistantContext } from '../../contexts/AIAssistantContext';
import WorkflowTimelineBottomBar from '../../components/common/WorkflowTimelineBottomBar';
// import { getScreenNameByPath } from '../../config/navigation';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
const EMPTY_CONFIRM_MODAL = {
  open: false,
  message: '',
  confirmText: 'Confirm',
  onConfirm: null,
  isDanger: false,
};

const DEFAULT_ESCALATION_RECIPIENTS = [
  {
    name: 'Regional Manager',
    email: 'regional.manager@company.com',
  },
];

const REGIONAL_MANAGER_CC_OPTIONS = [
  'john.smith@goodlabs.com',
  'sarah.johnson@goodlabs.com',
  'robert.williams@goodlabs.com',
  'emily.davis@goodlabs.com',
];

const TIMELINE_POLL_INTERVAL_MS = 5000;

const escapeHtml = (value = '') =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const appendCommentsToBody = (body = '', comments = '') => {
  const trimmedComments = String(comments || '').trim();
  if (!trimmedComments) return body || '';
  const commentBlock = `<p><strong>Additional comments:</strong><br/>${escapeHtml(trimmedComments).replaceAll('\n', '<br/>')}</p>`;
  return `${body || ''}${body ? '<br/><br/>' : ''}${commentBlock}`;
};

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};


const getTimelineVisuals = (step = {}, index = 0) => {
  const title = String(
    step?.title ||
    step?.name ||
    step?.eventName ||
    step?.stepName ||
    step?.step ||
    `step ${index + 1}`
  ).toLowerCase();
  const status = String(step?.status || '').toLowerCase();
  const type = String(step?.type || '').toLowerCase();
  const key = `${title} ${status} ${type}`;

  if (
    key.includes('agent started') ||
    key.includes('trigger') ||
    key.includes('workflow started')
  ) {
    return { accent: '#22c55e', icon: BoltIcon };
  }
  if (
    key.includes('metadata') ||
    key.includes('read') ||
    key.includes('retrieving prescription') ||
    key.includes('retrieve trx') ||
    key.includes('validating provider identity')
  ) {
    return { accent: '#a78bfa', icon: PsychologyAltRoundedIcon };
  }
  if (
    key.includes('retrieved hcp profile') ||
    key.includes('lookup') ||
    key.includes('internet') ||
    key.includes('master data') ||
    key.includes('public sources') ||
    key.includes('address performance') ||
    key.includes('verify address') ||
    key.includes('compare address')
  ) {
    return { accent: '#38bdf8', icon: PublicRoundedIcon };
  }
  if (
    key.includes('confidence') ||
    key.includes('decision') ||
    key.includes('evaluate') ||
    key.includes('evidence verified') ||
    key.includes('generating recommendation') ||
    key.includes('trx analysis') ||
    key.includes('agentic workflow')
  ) {
    return { accent: '#fb923c', icon: ShieldRoundedIcon };
  }
  if (key.includes('recommendation ready') || key.includes('recommendation generated')) {
    return { accent: '#f59e0b', icon: MonitorHeartRoundedIcon };
  }
  if (key.includes('human review') || key.includes('waiting for review')) {
    return { accent: '#f97316', icon: HourglassTopRoundedIcon };
  }
  if (key.includes('automatic approval completed')) {
    return { accent: '#16a34a', icon: CheckCircleRoundedIcon };
  }

  const fallbackVisuals = [
    { accent: '#38bdf8', icon: StreamRoundedIcon },
    { accent: '#22c55e', icon: BoltIcon },
    { accent: '#a78bfa', icon: PsychologyAltRoundedIcon },
    { accent: '#38bdf8', icon: PublicRoundedIcon },
    { accent: '#fb923c', icon: ShieldRoundedIcon },
  ];

  return fallbackVisuals[index] || fallbackVisuals[fallbackVisuals.length - 1];
};


const getWorkflowStepPresentation = (step = {}) => {
  const normalizedState = String(step?.state || step?.status || '').toLowerCase();
  const normalizedTitle = String(step?.title || '').toLowerCase();

  if (
    ['completed', 'complete', 'done', 'approved', 'verified', 'success'].includes(normalizedState)
  ) {
    return {
      state: 'completed',
      chipLabel: 'Completed',
      chipColor: '#16a34a',
      prefix: 'Done',
      showSpinner: false,
    };
  }

  if (
    [
      'running',
      'active',
      'in_progress',
      'in progress',
    ].includes(normalizedState)
  ) {
    return {
      state: 'running',
      chipLabel: 'In Progress',
      chipColor: '#f97316',
      prefix: 'Live',
      showSpinner: true,
    };
  }

  if (
    [
      'pending_review',
      'waiting',
      'waiting_for_review',
      'human_review_required',
      'review_required',
      'awaiting_review',
    ].includes(normalizedState) ||
    normalizedTitle.includes('waiting') ||
    normalizedTitle.includes('review')
  ) {
    return {
      state: 'awaiting_review',
      chipLabel: 'Awaiting Review',
      chipColor: '#c2410c',
      prefix: 'Waiting',
      showSpinner: false,
    };
  }

  return {
    state: 'idle',
    chipLabel: 'Queued',
    chipColor: '#64748b',
    prefix: '',
    showSpinner: false,
  };
};

const normalizeOverallStatus = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) return '';
  if (
    normalized === 'waiting_for_manual_review' ||
    normalized === 'waiting_for_review' ||
    normalized === 'pending_review' ||
    normalized === 'human_review_required' ||
    normalized === 'review_required' ||
    normalized === 'awaiting_review'
  ) {
    return 'AWAITING_REVIEW';
  }
  if (normalized === 'completed' || normalized === 'complete' || normalized === 'done') {
    return 'COMPLETED';
  }
  if (normalized === 'failed' || normalized === 'error' || normalized === 'rejected') {
    return 'FAILED';
  }
  return String(value).toUpperCase();
};

const isCompletedWorkflowState = (state) =>
  ['completed', 'complete', 'done', 'approved', 'human_review_required','verified', 'success'].includes(
    String(state || '').toLowerCase()
  );

const areAllTimelineStepsCompleted = (steps = []) =>
  steps.length > 0 && steps.every((step) => isCompletedWorkflowState(step?.state || step?.status));

const extractTimelineItems = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.payload)) return response.payload;
  if (Array.isArray(response?.events)) return response.events;
  if (Array.isArray(response?.steps)) return response.steps;
  if (Array.isArray(response?.workflow)) return response.workflow;
  if (Array.isArray(response?.workflowSteps)) return response.workflowSteps;
  if (Array.isArray(response?.timeline)) return response.timeline;
  if (Array.isArray(response?.agentStatus)) return response.agentStatus;
  if (Array.isArray(response?.agentStatuses)) return response.agentStatuses;
  if (Array.isArray(response?.statusList)) return response.statusList;
  if (Array.isArray(response?.data?.result)) return response.data.result;
  if (Array.isArray(response?.data?.payload)) return response.data.payload;
  if (Array.isArray(response?.data?.events)) return response.data.events;
  if (Array.isArray(response?.data?.steps)) return response.data.steps;
  if (Array.isArray(response?.data?.workflow)) return response.data.workflow;
  if (Array.isArray(response?.data?.workflowSteps)) return response.data.workflowSteps;
  if (Array.isArray(response?.data?.timeline)) return response.data.timeline;
  if (Array.isArray(response?.data?.agentStatus)) return response.data.agentStatus;
  if (Array.isArray(response?.data?.agentStatuses)) return response.data.agentStatuses;
  if (Array.isArray(response?.data?.statusList)) return response.data.statusList;
  return [];
};

const isTimelineObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const formatTimelineKey = (key = '') =>
  String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const AGENT_STEP_LABELS = {
  data_steward_agent_workflow: 'Data steward agent workflow',
  workflow_started: 'Agent started review',
  standardizing_requested_address: 'Standardizing requested address',
  internet_search: 'Internet search',
  web_search: 'Web search',
  compare_address: 'Comparing address',
  verify_address_presence: 'Verifying requested address',
  retrieve_trx_data: 'Retrieving prescription data',
  trx_analysis: 'Analyzing prescription volume',
  agentic_workflow: 'Waiting for human review',
  recommendation_generated: 'Recommendation ready',
};

const getAgentStepKey = (item = {}, index = 0) =>
  item.step ||
  item.stepName ||
  item.eventName ||
  item.name ||
  item.title ||
  item.workflowName ||
  item.id ||
  `agent_status_${index}`;

const getAgentStepLabel = (item = {}, index = 0) => {
  const stepKey = getAgentStepKey(item, index);
  return (
    item.step ||
    item.stepName ||
    item.eventName ||
    item.name ||
    item.title ||
    AGENT_STEP_LABELS[stepKey] ||
    formatTimelineKey(stepKey || 'Agent Status')
  );
};

const getAgentStatusTime = (item = {}) => {
  const value =
    item.updated_at || item.updatedAt || item.created_at || item.createdAt || item.timestamp;
  const time = Date.parse(value);
  return Number.isNaN(time) ? 0 : time;
};

const getAgentCreatedTime = (item = {}) => {
  const value =
    item.created_at || item.createdAt || item.updated_at || item.updatedAt || item.timestamp;
  const time = Date.parse(value);
  return Number.isNaN(time) ? 0 : time;
};

const formatAgentStatusMessage = (message) => {
  if (!message) return '';
  if (typeof message !== 'string') return String(message);

  try {
    const parsed = JSON.parse(message);
    const recommendation = parsed.recommendation ? formatTimelineKey(parsed.recommendation) : '';
    const reason = parsed.reason || '';

    if (recommendation && reason) return `${recommendation}: ${reason}`;
    if (reason) return reason;
    if (recommendation) return recommendation;
  } catch {
    return message;
  }

  return message;
};

const normalizeAgentStatusEvents = (items = []) => {
  return items
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => ({
      ...item,
      __timelineOrder: index,
      __createdTime: getAgentCreatedTime(item),
      __statusTime: getAgentStatusTime(item),
      title: getAgentStepLabel(item, index),
      subtitle:
        formatAgentStatusMessage(
          item.message || item.description || item.detail || item.summary || item.statusMessage
        ) || 'Agent workflow status received.',
      state:
        item.status ||
        item.state ||
        item.workflowStatus ||
        item.executionStatus ||
        (getAgentStepKey(item, index) === 'agentic_workflow' ? 'awaiting_review' : undefined),
    }))
    .sort((a, b) => {
      const createdDelta = (a.__createdTime || 0) - (b.__createdTime || 0);
      if (createdDelta !== 0) return createdDelta;

      const statusDelta = (a.__statusTime || 0) - (b.__statusTime || 0);
      if (statusDelta !== 0) return statusDelta;

      return (a.__timelineOrder || 0) - (b.__timelineOrder || 0);
    })
    .map(({ __timelineOrder, __createdTime, __statusTime, ...item }) => item);
};

const getTimelineStateFromValue = (value) => {
  if (isTimelineObject(value)) {
    return (
      value.state ||
      value.status ||
      value.workflowStatus ||
      value.executionStatus ||
      value.stepStatus ||
      value.completedStatus ||
      ''
    );
  }

  if (typeof value === 'boolean') return value ? 'completed' : 'idle';
  return value || '';
};

const getTimelineMessageFromValue = (value, fallback = '') => {
  if (isTimelineObject(value)) {
    return (
      value.subtitle ||
      value.description ||
      value.detail ||
      value.message ||
      value.summary ||
      value.reason ||
      value.error ||
      value.statusMessage ||
      fallback
    );
  }

  if (value === true) return 'Completed by agent workflow.';
  if (value === false) return 'Waiting for this workflow step.';
  return value ? String(value) : fallback;
};

const buildTimelineItemsFromStatusObject = (response) => {
  const source =
    (isTimelineObject(response?.data) && response.data) ||
    (isTimelineObject(response?.result) && response.result) ||
    (isTimelineObject(response?.payload) && response.payload) ||
    response;

  if (!isTimelineObject(source)) return [];

  const timelineContainer =
    [
      source.agentStatus,
      source.workflowStatus,
      source.workflow,
      source.steps,
      source.statusList,
      source.timeline,
      source.data,
      source.result,
      source.payload,
    ].find(isTimelineObject) || source;

  if (!isTimelineObject(timelineContainer)) return [];

  const ignoredKeys = new Set([
    'id',
    'requestId',
    'request_id',
    'batchId',
    'batch_id',
    'mdmId',
    'mdm_id',
    'createdAt',
    'created_at',
    'updatedAt',
    'updated_at',
    'lastUpdated',
    'last_updated',
    'timestamp',
    'time',
  ]);

  const entries = Object.entries(timelineContainer)
    .filter(([key, value]) => !ignoredKeys.has(key) && value !== null && value !== undefined)
    .map(([key, value]) => {
      const title =
        (isTimelineObject(value) &&
          (value.title || value.name || value.eventName || value.stepName || value.workflowName)) ||
        formatTimelineKey(key);

      return {
        title,
        subtitle: getTimelineMessageFromValue(value, `Agent status value: ${String(value)}`),
        status: getTimelineStateFromValue(value),
        active: isTimelineObject(value) ? value.active !== false : true,
      };
    });

  if (entries.length > 0) return entries;

  return [
    {
      title: 'Agent Status',
      subtitle: getTimelineMessageFromValue(source, 'Agent workflow status received.'),
      status: getTimelineStateFromValue(source) || 'running',
      active: true,
    },
  ];
};

const normalizeTimelineSteps = (items = []) =>
  items.filter(Boolean).map((item, index) => {
    const visuals = getTimelineVisuals(item, index);
    const title =
      item?.title ||
      item?.name ||
      item?.eventName ||
      item?.stepName ||
      item?.step ||
      `Step ${index + 1}`;
    const subtitle =
      item?.subtitle ||
      item?.description ||
      item?.detail ||
      item?.message ||
      item?.summary ||
      'Timeline event received from workflow service.';

    return {
      ...item,
      title: /^\d+\./.test(String(title)) ? String(title) : `${index + 1}. ${title}`,
      subtitle: String(subtitle),
      accent: visuals.accent,
      icon: visuals.icon,
      active: item?.active !== false,
      state: item?.state || item?.status || (item?.active === false ? 'idle' : 'completed'),
    };
  });

const getWorkflowQueryText = (...values) => {
  for (const value of values) {
    if (!value) continue;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }
  return '';
};
const WorkflowNode = ({ title, subtitle, accent, icon: Icon, active, state, index }) => {
  const stateMeta = getWorkflowStepPresentation({ title, state });
  const isRunning = stateMeta.state === 'running';
  const isCompleted = stateMeta.state === 'completed';
  const isIdle = stateMeta.state === 'idle';
  const nodeColor = isIdle ? '#94a3b8' : accent;
  const iconBackground = isIdle
    ? alpha('#cbd5e1', 0.28)
    : isRunning
      ? alpha(accent, 0.2)
      : alpha(accent, 0.1);
  const iconBorder = isIdle
    ? alpha('#94a3b8', 0.32)
    : isRunning
      ? alpha(accent, 0.55)
      : alpha(accent, 0.18);
  const textColor = isIdle ? '#94a3b8' : isRunning ? '#111827' : '#0f172a';
  const subtitleColor = isIdle ? '#94a3b8' : isRunning ? '#4c1d95' : '#475569';

  return (
    <Stack
      direction="row"
      spacing={2.25}
      sx={{
        alignItems: 'flex-start',
        opacity: 0,
        transform: 'translateY(18px)',
        animation: `workflowNodeReveal 560ms ease-out ${index * 90}ms forwards`,
        '@keyframes workflowNodeReveal': {
          '0%': {
            opacity: 0,
            transform: 'translateY(18px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.25 }}>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: iconBackground,
            color: nodeColor,
            border: '1px solid',
            borderColor: iconBorder,
            boxShadow: isRunning
              ? `0 6px 18px ${alpha(accent, 0.22)}, 0 0 0 4px ${alpha(accent, 0.14)}`
              : 'none',
            position: 'relative',
            '&::after': isRunning
              ? {
                content: '""',
                position: 'absolute',
                inset: -6,
                borderRadius: '50%',
                border: `2px solid ${alpha(accent, 0.32)}`,
                borderTopColor: accent,
                borderRightColor: accent,
                borderBottomColor: alpha(accent, 0.55),
                animation: 'timelineSpinnerRotate 1s linear infinite',
              }
              : {},
            '@keyframes timelineSpinnerRotate': {
              '0%': {
                transform: 'rotate(0deg)',
              },
              '100%': {
                transform: 'rotate(360deg)',
              },
            },
          }}
        >
          <Icon
            sx={{
              
              animation: isRunning ? 'timelineIconGlow 1.1s ease-in-out infinite' : 'none',
              '@keyframes timelineIconGlow': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.62 },
                '100%': { opacity: 1 },
              },
            }}
          />
        </Avatar>
        <Box
          sx={{
            width: 2,
            flex: 1,
            minHeight: 54,
            mt: 1.25,
            borderRadius: 999,
            background: isIdle
              ? `linear-gradient(180deg, ${alpha('#cbd5e1', 0.9)} 0%, ${alpha(
                '#e2e8f0',
                0.5
              )} 100%)`
              : `linear-gradient(180deg, ${alpha(accent, isCompleted ? 0.62 : 0.78)} 0%, ${alpha(
                accent,
                0.12
              )} 100%)`,
          }}
        />
      </Box>
      <Box sx={{ pb: 3.25, pt: 0.1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography
            variant="subtitle2"
            sx={{ color: textColor, fontWeight: 600,  lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          {isCompleted ? (
            <Chip
              label="Completed"
              size="small"
              sx={{
                height: 24,
                color: '#15803d',
                bgcolor: alpha('#22c55e', 0.12),
                borderRadius: '999px',
                fontWeight: 600,
                border: '1px solid',
                borderColor: alpha('#22c55e', 0.24),
              }}
            />
          ) : null}
          {isRunning ? (
            <Chip
              icon={
                <CircularProgress size={12} thickness={6} sx={{ color: `${accent} !important` }} />
              }
              label={stateMeta.chipLabel}
              size="small"
              sx={{
                height: 24,
                color: accent,
                bgcolor: alpha(accent, 0.12),
                borderRadius: '999px',
                fontWeight: 600,
                border: '1px solid',
                borderColor: alpha(accent, 0.2),
                '& .MuiChip-icon': {
                  marginLeft: '8px',
                  marginRight: '-4px',
                },
              }}
            />
          ) : null}
        </Stack>
        <Typography
          // variant="body2"
          sx={{ mt: 0.65, color: subtitleColor, lineHeight: 1.7, maxWidth: 560,  }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
};

WorkflowNode.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  active: PropTypes.bool,
  state: PropTypes.string,
  index: PropTypes.number,
};

WorkflowNode.defaultProps = {
  active: false,
  state: 'idle',
  index: 0,
};

// const getCurrentUser = () => {
//   const rawUser = sessionStorage.getItem('user') || localStorage.getItem('user');
//   if (!rawUser) return {};
// 
//   try {
//     return JSON.parse(rawUser);
//   } catch {
//     return {};
//   }
// };

const defaultRequestDetails = {
  requestType: 'New',
  recordType: 'Account',
  requestId: '',
  accountId: '',
  accountName: '',
  requestName: '',
  source: '',
};

const defaultRequestorDetails = {
  name: '',
  email: '',
  empNumber: '',
  profName: '',
};

const defaultHcpDetails = {
  firstName: '',
  middleName: '',
  lastName: '',
  suffix: '',
  gender: '',
  birthYr: '',
  primarySpecialty: '',
  subSpecialty: '',
  primaryProfession: '',
  status: '',
  okIndvId: '',
  npiId: '',
  imsId: '',
  amaId: '',
  slnId: '',
  isGoldenUniverse: '',
  mdmId: '',
  source: '',
  primaryAddress: {
    addrLn1: '',
    addrLn2: '',
    cityNm: '',
    stCd: '',
    zipCd: ['', ''],
  },
  addrDcrObj: {
    addrLn1: '',
    addrLn2: '',
    cityNm: '',
    stCd: '',
    zipCd: ['', ''],
    busNm: '',
    category: '',
    setting: '',
    affltnType: '',
    primary: '',
    masterPrimary: '',
    isSampleable: '',
    statusLabel: '',
    addrId: '',
  },
};

const defaultFacetOptions = {
  professions: [],
  specialities: [],
  status: [],
  state: [],
};

const ADDRESS_CATEGORY_OPTIONS = [
  'Other / Unknown',
  'Pharmacy',
  'Residential (NH, HH, etc.)',
  'Corporate / Contracting',
  'Outpatient',
  'Institutional',
];

const ADDRESS_SETTING_OPTIONS = ['Office', 'Both', 'Home', 'PO Box', 'Unknown'];
const ADDRESS_SAMPLEABLE_OPTIONS = ['Yes', 'No'];
const ADDRESS_STATUS_OPTIONS = ['Active', 'Inactive'];
const ADDRESS_MASTER_PRIMARY_OPTIONS = ['true', 'false'];

const normalizeRecordType = (value) => {
  const rawValue = Array.isArray(value) ? value[0] : value;
  return String(rawValue || 'Account').split('_')[0];
};

const normalizeZipCode = (value) => {
  if (Array.isArray(value)) {
    return [value[0] || '', value[1] || ''];
  }
  if (value === null || value === undefined || value === '') {
    return ['', ''];
  }
  return [value, ''];
};

const buildAddressDcrObject = (addressDetails = {}) => ({
  addrLn1: addressDetails?.addrLn1 || '',
  addrLn2: addressDetails?.addrLn2 || '',
  cityNm: addressDetails?.cityNm || '',
  stCd: addressDetails?.stCd || '',
  zipCd: normalizeZipCode(addressDetails?.zipCd),
  busNm: addressDetails?.busNm || '',
  category: addressDetails?.category || '',
  setting: addressDetails?.setting || '',
  affltnType: addressDetails?.affltnType || '',
  primary:
    addressDetails?.primary === true
      ? 'true'
      : addressDetails?.primary === false
        ? 'false'
        : addressDetails?.primary || '',
  masterPrimary:
    addressDetails?.masterPrimary === true
      ? 'true'
      : addressDetails?.masterPrimary === false
        ? 'false'
        : addressDetails?.masterPrimary || '',
  isSampleable: addressDetails?.isSampleable || '',
  statusLabel: addressDetails?.statusLabel || addressDetails?.status || '',
  addrId: addressDetails?.addrId || '',
});

const createInitialAddressDcrState = () => ({
  ...buildAddressDcrObject(),
  statusLabel: 'Active',
  masterPrimary: 'false',
});

const HCP_FIELD_MAP = {
  firstName: ['firstName'],
  middleName: ['middleName'],
  lastName: ['lastName'],
  suffix: ['suffix'],
  birthYr: ['birthYr'],
  gender: ['gender'],
  primarySpecialty: ['primarySpecialty'],
  primaryProfession: ['primaryProfession'],
  status: ['status'],
  okIndvId: ['okIndvId'],
  npiId: ['npiId'],
  imsId: ['imsId'],
  amaId: ['amaId'],
  slnId: ['slnId'],
  isGoldenUniverse: ['isGoldenUniverse'],
  addrLn1: ['primaryAddress', 'addrLn1'],
  addrLn2: ['primaryAddress', 'addrLn2'],
  cityNm: ['primaryAddress', 'cityNm'],
  stCd: ['primaryAddress', 'stCd'],
  zipCd: ['primaryAddress', 'zipCd', 0],
  zip4: ['primaryAddress', 'zipCd', 1],
  category: ['addrDcrObj', 'category'],
  setting: ['addrDcrObj', 'setting'],
  isSampleable: ['addrDcrObj', 'isSampleable'],
  statusLabel: ['addrDcrObj', 'statusLabel'],
  masterPrimary: ['addrDcrObj', 'masterPrimary'],
};

const setNestedValue = (source, path, value) => {
  const clone = structuredClone(source);
  let current = clone;
  for (let i = 0; i < path.length - 1; i += 1) {
    current = current[path[i]];
  }
  current[path[path.length - 1]] = value;
  return clone;
};

const normalizeRequestLine = (requestLine) => {
  if (!requestLine || typeof requestLine !== 'object') return [];
  if (Array.isArray(requestLine)) return requestLine;
  return Object.keys(requestLine).map((key) => ({
    id: key,
    ...requestLine[key],
  }));
};

const applyEditedFields = (details, lines) => {
  let nextDetails = structuredClone(details);
  lines.forEach((line) => {
    const path = HCP_FIELD_MAP[line.fieldName];
    if (!path) return;
    nextDetails = setNestedValue(nextDetails, path, line.newValue ?? '');
  });
  return nextDetails;
};

const mapHcpResponse = (response) => {
  const source =
    response?.data ||
    response?.dcrRequest ||
    (Array.isArray(response?.dcrRequests) ? response.dcrRequests[0] : null) ||
    response ||
    {};
  const account = source?.accountDetails || {};
  const lines = normalizeRequestLine(source?.requestLine);
  const details = {
    firstName: account.firstName || account.fName || account.first_name || '',
    middleName: account.middleName || account.mName || account.middle_name || '',
    lastName: account.lastName || account.lName || account.last_name || '',
    suffix: account.suffix || '',
    gender: account.gender || '',
    birthYr: account.birthYr || '',
    primarySpecialty: account.specialty || '',
    subSpecialty: account.subSpecialty || account.subSpeciality || '',
    primaryProfession: account.profession || '',
    status: account.status || '',
    okIndvId: account.onekeyId || '',
    npiId: account.npiId || '',
    imsId: account.imsId || '',
    amaId: account.amaId || '',
    slnId: account.slnId || '',
    isGoldenUniverse: account.isGoldenUniverse || '',
    mdmId: source?.mdmId || '',
    primaryAddress: {
      addrLn1: account.addressLine || '',
      addrLn2: account.addressLine2 || null,
      cityNm: account.city || '',
      stCd: account.state || '',
      zipCd: [account.zip || '', account.zip4 || null],
    },
    addrDcrObj: buildAddressDcrObject(source?.addressDetails),
  };

  return {
    requestDetails: {
      requestType: source?.requestType || 'Edit',
      recordType: normalizeRecordType(source?.recordType),
      requestId: source?.requestId || '',
      accountId: source?.accountId || source?.mdmId || '',
      accountName: source?.accountName || '',
      requestName: source?.requestName || '',
      source: source?.source || '',
    },
    requestorDetails: {
      name: source?.ownerDetails?.name || '',
      email: source?.ownerDetails?.email || '',
      empNumber: source?.ownerDetails?.empNumber || '',
      profName: source?.ownerDetails?.profileName || '',
    },
    hcpDetails: applyEditedFields(details, lines),
    baseHcpDetails: details,
    editedFields: lines.map((line) => ({
      ...line,
      id: line.id || null,
      mdmStatus: line.mdmStatus || null,
      resolutionNote: line.resolutionNote || '',
    })),
    tableData: {
      srcRecord: formatTableData(source?.srcRecord, 'src'),
      address: formatTableData(
        source?.addressDetailsMap || source?.accountAddressDetails || source?.allAddressDetails,
        'addr'
      ),
      specialty: formatTableData(source?.specialtyDetails, 'spc'),
      subSpecialty: formatTableData(source?.subSpecialtyDetails, 'subspec'),
      sln: formatTableData(source?.slnDetails, 'sln'),
      isn: formatTableData(source?.isn || source?.isnDetails, 'isn'),
    },
  };
};

const buildSavePayload = (
  requestDetails,
  requestorDetails,
  hcpDetails,
  baseHcpDetails,
  editedFields
) => {
  const toNullableValue = (value) => (value === '' || value === undefined ? null : value);
  const toRequestLineValue = (value) => (value === undefined || value === null ? '' : value);
  const finalFields = [...editedFields];
  const fieldsToCheck = [
    'firstName',
    'middleName',
    'lastName',
    'suffix',
    'birthYr',
    'gender',
    'primarySpecialty',
    'primaryProfession',
    'status',
  ];

  fieldsToCheck.forEach((field) => {
    const newValue = hcpDetails[field];
    const oldValue = baseHcpDetails[field];

    if (newValue !== oldValue) {
      const idx = finalFields.findIndex((f) => f.fieldName === field);
      if (idx !== -1) {
        finalFields[idx] = {
          ...finalFields[idx],
          newValue: newValue ?? '',
          mdmStatus: finalFields[idx].mdmStatus || 'Accepted',
        };
      } else {
        finalFields.push({
          fieldName: field,
          oldValue: oldValue || '',
          newValue: newValue ?? '',
          mdmStatus: 'Accepted',
          resolutionNote: null,
        });
      }
    }
  });

  const payload = {
    requestType: requestDetails.requestType,
    recordType: requestDetails.recordType,
    accountId: requestDetails.accountId,
    accountName: requestDetails.accountName,
    mdmAccountType: 'HCP',
    mdmId: hcpDetails.mdmId || requestDetails.accountId,
    requestName: requestDetails.requestName || undefined,
    addressDetails: null,
    mdmAddressId: null,
    requestLine: [],
    ownerDetails: {
      name: requestorDetails.name,
      email: requestorDetails.email,
      profileName: requestorDetails.profName,
      empNumber: requestorDetails.empNumber,
    },
    accountDetails: {
      name: requestDetails.accountName,
      firstName: hcpDetails.firstName || null,
      middleName: hcpDetails.middleName || null,
      lastName: hcpDetails.lastName || null,
      suffix: hcpDetails.suffix || '',
      gender: hcpDetails.gender || null,
      addressLine: hcpDetails.primaryAddress.addrLn1 || null,
      addressLine2: toNullableValue(hcpDetails.primaryAddress.addrLn2),
      city: hcpDetails.primaryAddress.cityNm || null,
      state: hcpDetails.primaryAddress.stCd || null,
      zip: hcpDetails.primaryAddress.zipCd[0] || null,
      zip4: toNullableValue(hcpDetails.primaryAddress.zipCd[1]),
      birthYr: hcpDetails.birthYr || null,
      onekeyId: hcpDetails.okIndvId || null,
      amaId: hcpDetails.amaId || null,
      imsId: hcpDetails.imsId || null,
      npiId: hcpDetails.npiId || null,
      slnId: hcpDetails.slnId || null,
      profId: hcpDetails.mdmId || requestDetails.accountId || null,
      specialty: hcpDetails.primarySpecialty || null,
      profession: hcpDetails.primaryProfession || null,
      status: hcpDetails.status || null,
      isGoldenUniverse: hcpDetails.isGoldenUniverse || 'N/A',
    },
  };

  if (requestDetails.recordType === 'Address') {
    const addressFieldNames = [
      'addrLn1',
      'addrLn2',
      'cityNm',
      'stCd',
      'category',
      'setting',
      'isSampleable',
      'statusLabel',
      'masterPrimary',
    ];
    const normalizedAddress = buildAddressDcrObject(hcpDetails.addrDcrObj);
    const normalizedBaseAddress = buildAddressDcrObject(baseHcpDetails.addrDcrObj);

    addressFieldNames.forEach((fieldName) => {
      const newValue = normalizedAddress[fieldName] ?? '';
      const oldValue = normalizedBaseAddress[fieldName] ?? '';
      if (newValue === oldValue) return;

      const idx = finalFields.findIndex((field) => field.fieldName === fieldName);
      const nextField = {
        fieldName,
        oldValue,
        newValue,
        mdmStatus:
          fieldName === 'masterPrimary' || fieldName === 'isGoldenUniverse'
            ? 'Accepted'
            : finalFields[idx]?.mdmStatus || null,
        resolutionNote: finalFields[idx]?.resolutionNote || null,
      };

      if (idx !== -1) {
        finalFields[idx] = { ...finalFields[idx], ...nextField };
      } else {
        finalFields.push(nextField);
      }
    });

    const zipChanged = normalizedAddress.zipCd[0] !== normalizedBaseAddress.zipCd[0];
    if (zipChanged) {
      const idx = finalFields.findIndex((field) => field.fieldName === 'zipCd');
      const nextField = {
        fieldName: 'zipCd',
        oldValue: normalizedBaseAddress.zipCd[0] ?? '',
        newValue: normalizeZipCode(normalizedAddress.zipCd).filter(Boolean).join('-'),
        mdmStatus: finalFields[idx]?.mdmStatus || null,
        resolutionNote: finalFields[idx]?.resolutionNote || null,
      };

      if (idx !== -1) {
        finalFields[idx] = { ...finalFields[idx], ...nextField };
      } else {
        finalFields.push(nextField);
      }
    }

    const hasAddressContent = Object.entries(normalizedAddress).some(([key, value]) => {
      if (key === 'addrId') return false;
      if (Array.isArray(value)) return value.some(Boolean);
      return value !== '';
    });

    payload.addressDetails = hasAddressContent
      ? {
        addrLn1: normalizedAddress.addrLn1 || '',
        addrLn2: normalizedAddress.addrLn2 || '',
        cityNm: normalizedAddress.cityNm || '',
        stCd: normalizedAddress.stCd || '',
        zipCd: normalizeZipCode(normalizedAddress.zipCd).filter(Boolean),
        busNm: normalizedAddress.busNm || '',
        category: normalizedAddress.category || '',
        setting: normalizedAddress.setting || '',
        affltnType: normalizedAddress.affltnType || '',
        primary: normalizedAddress.primary || '',
        masterPrimary: normalizedAddress.masterPrimary || '',
        isSampleable: normalizedAddress.isSampleable || '',
        statusLabel: normalizedAddress.statusLabel || '',
      }
      : null;
    payload.mdmAddressId = normalizedAddress.addrId || null;
  }

  payload.requestLine = finalFields.map((line) => ({
    id: line.id || undefined,
    fieldName: line.fieldName,
    oldValue: toRequestLineValue(line.oldValue),
    newValue: toRequestLineValue(line.newValue),
    resolutionNote: line.resolutionNote || null,
    mdmStatus: line.mdmStatus || null,
  }));

  if (!payload.requestName) delete payload.requestName;
  return payload;
};

const formatTableData = (state, ref, otherData = []) => {
  if (!state) return [];
  if (Array.isArray(state)) return state;
  if (typeof state !== 'object') return [];

  const array = [];
  Object.keys(state).forEach((i) => {
    if (ref === 'isn') {
      Object.keys(state[i] || {}).forEach((j) => {
        const isn = { ...(state[i]?.[j] || {}) };
        isn.Id = j;
        isn.isnCode = i;
        array.push(isn);
      });
    } else if (ref === 'sln') {
      const sln = { ...(state[i] || {}) };
      sln.slnAuthNbr = i;
      array.push(sln);
    } else if (ref === 'addr') {
      const addr = { ...(state[i] || {}) };
      addr.addrId = i;
      if (Array.isArray(otherData) && otherData.length > 0) {
        addr.overriddenAddressFields = [];
        otherData.forEach((item) => {
          if (typeof item !== 'string') return;
          if (item.length === 41 && addr.addrId === item.substring(5)) {
            addr.overriddenAddress = item.substring(5);
          } else if (item.length > 41 && addr.addrId === item.substring(5, 41)) {
            addr.overriddenAddressFields.push(item.substring(42));
          }
        });
      }
      array.push(addr);
    } else if (ref === 'spc' || ref === 'subspec') {
      const spc = { ...(state[i] || {}) };
      spc.rank = i;
      array.push(spc);
    } else if (ref === 'src') {
      const src = { ...(state[i] || {}) };
      src.srname = i;
      array.push(src);
    }
  });

  return array;
};

const formatScalar = (value) => {
  if (Array.isArray(value)) {
    const filtered = value.filter((item) => item !== null && item !== undefined && item !== '');
    return filtered.length > 0 ? filtered.join(' - ') : '-';
  }
  if (typeof value === 'boolean') return String(value);
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
};

const parseWorkflowMessage = (message) => {
  if (!message || typeof message !== 'string') return null;
  try {
    return JSON.parse(message);
  } catch {
    return null;
  }
};

const getConfidenceMeta = (score) => {
  const numericScore = Number.parseFloat(score);
  const value = Number.isFinite(numericScore) ? Math.max(0, Math.min(100, numericScore)) : 75;

  if (value >= 90) {
    return { value, label: 'Auto Approve', color: '#16a34a', bg: '#dcfce7' };
  }
  if (value >= 75) {
    return { value, label: 'Approve Recommended', color: '#16a34a', bg: '#dcfce7' };
  }
  if (value >= 60) {
    return { value, label: 'Human Review Required', color: '#d97706', bg: '#fef3c7' };
  }
  if (value >= 40) {
    return { value, label: 'Low Confidence', color: '#ea580c', bg: '#ffedd5' };
  }
  return { value, label: 'Reject Recommended', color: '#dc2626', bg: '#fee2e2' };
};

const getRecommendationLabel = (recommendation, fallback = 'Human Review Required') => {
  if (!recommendation) return fallback;
  return formatTimelineKey(recommendation);
};

const buildRecommendationBullets = (workflow = {}, requestRecordType = 'Address') => {
  const oldLocation = [workflow.old_cityNm, workflow.old_stcd, workflow.old_zip].filter(Boolean).join(', ');
  const newLocation = [workflow.new_cityNm, workflow.new_stcd, workflow.new_zip].filter(Boolean).join(', ');
  const oldTrx = workflow.old_trx ?? workflow.oldTrx;
  const newTrx = workflow.new_trx ?? workflow.newTrx;

  const geographicPoint =
    oldLocation && newLocation
      ? `Geographic: primary location changes from ${oldLocation} to ${newLocation}.`
      : 'Geographic: location change requires validation against source records.';

  const prescriptionPoint =
    oldTrx !== undefined || newTrx !== undefined
      ? `Prescription volume: old TRX for old zip ${oldTrx ?? '-'} vs new TRX for new zip ${newTrx ?? '-'}.`
      : 'Prescription volume: data is not available for meaningful automated comparison.';

  return [
    geographicPoint,
    prescriptionPoint,
    requestRecordType === 'Address'
      ? 'Insufficient automated evidence to approve the address change.'
      : 'Recommendation confidence is based on workflow evidence and LLM scoring.',
    'Human review is required to make the final decision.',
  ];
};

const isMasterPrimaryAddress = (row) =>
  row?.masterPrimary === true || String(row?.masterPrimary).toLowerCase() === 'true';

const sortMasterPrimaryFirst = (rows) => {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => {
    const primarySort = Number(isMasterPrimaryAddress(b)) - Number(isMasterPrimaryAddress(a));
    if (primarySort !== 0) return primarySort;

    const leftRank = Number(a?.rank ?? Number.NEGATIVE_INFINITY);
    const rightRank = Number(b?.rank ?? Number.NEGATIVE_INFINITY);
    return rightRank - leftRank;
  });
};

const getTabValue = (row, keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return '-';
};

const HCP_SOURCE_COLUMNS = [
  { label: 'Source Name', keys: ['source', 'srname'] },
  { label: 'External Id', keys: ['externalId'] },
  { label: 'Prof Id', keys: ['srcProfId'] },
  { label: 'Merge Status', keys: ['mergeStatus'] },
  { label: 'First Name', keys: ['firstName'] },
  { label: 'Middle Name', keys: ['middleName'] },
  { label: 'Last Name', keys: ['lastName'] },
  { label: 'Suffix', keys: ['suffix'] },
  { label: 'Birth Date', keys: ['birthDate'] },
  { label: 'Gender', keys: ['gender'] },
  { label: 'Degree', keys: ['degree'] },
  { label: 'Graduation Year', keys: ['graduationYear'] },
  { label: 'Medical School', keys: ['medSchoolDesc'] },
  {
    label: 'Specialty',
    render: (row) => {
      const value = row?.spcl;
      if (value && typeof value === 'object') {
        const labels = Object.values(value)
          .map((item) => item?.spclDesc)
          .filter(Boolean);
        return labels.length > 0 ? labels.join(', ') : '-';
      }
      return formatScalar(value);
    },
  },
  { label: 'Phone', keys: ['cellPhone'] },
  { label: 'Updated Date', keys: ['updatedDate'] },
];

const HCP_ADDRESS_COLUMNS = [
  { label: 'Source', keys: ['source'] },
  { label: 'Rank', keys: ['rank'] },
  { label: 'Address Line 1', keys: ['addrLn1'] },
  { label: 'City', keys: ['cityNm'] },
  { label: 'State', keys: ['stCd'] },
  { label: 'Zip', keys: ['zipCd'] },
  { label: 'Master Primary', keys: ['masterPrimary'] },
  { label: 'External Id', keys: ['externalId'] },
  { label: 'Veeva Primary', keys: ['primary'] },
  { label: 'Client Preferred', keys: ['clientPreferred', 'primary'] },
  { label: 'Ok Best', keys: ['okBestAddr'] },
  { label: 'Address Line 2', keys: ['addrLn2'] },
  { label: 'Address Id', keys: ['addrId'] },
  { label: 'Status', keys: ['statusLabel', 'status'] },
  { label: 'Has State Sanction', keys: ['hasStateSanction'] },
  { label: 'Is Sampleable', keys: ['isSampleable'] },
  { label: 'Setting', keys: ['setting'] },
  { label: 'Category', keys: ['category'] },
  { label: 'Business Name', keys: ['busNm'] },
  { label: 'Rep Managed', keys: ['repManaged'] },
  { label: 'Affiliation Type', keys: ['affltnType'] },
  { label: 'Cell Phone', keys: ['cellPhone'] },
  { label: 'Telephone', keys: ['telephone'] },
  { label: 'Fax', keys: ['fax'] },
  { label: 'Street Front Id', keys: ['strFrontId'] },
  { label: 'Latitude', keys: ['latitude'] },
  { label: 'Longitude', keys: ['longitude'] },
  { label: 'Overridden', keys: ['overridden'] },
];

const HCP_SPECIALTY_COLUMNS = [
  { label: 'Rank', keys: ['rank'] },
  { label: 'Specialty Description', keys: ['spclDesc', 'specialty', 'speciality', 'value'] },
  { label: 'Specialty Code', keys: ['spclCode'] },
  { label: 'Specialty Group', keys: ['clientSpclGroup'] },
  { label: 'Professional Description', keys: ['prfsnDesc'] },
];

const HCP_SLN_COLUMNS = [
  { label: 'Auth Number', keys: ['slnAuthNbr'] },
  { label: 'Status', keys: ['authnStatDesc', 'status'] },
  { label: 'State Code', keys: ['stCd'] },
  { label: 'Board ID', keys: ['stBrdId'] },
  { label: 'Board Name', keys: ['stBrdNm'] },
  { label: 'Type ID', keys: ['stLicTypId'] },
  { label: 'Type Name', keys: ['stLicTypNm'] },
  { label: 'Privilege ID', keys: ['stLicPrivlId'] },
  { label: 'Privilege', keys: ['stLicPrivlNm'] },
  { label: 'Certification Name', keys: ['stLicCertNm'] },
  { label: 'Privilege Rank', keys: ['stLicTypPrivlRnkNbr'] },
  { label: 'Effective Date', keys: ['authnEffDt'] },
  { label: 'Expiration Date', keys: ['authnTrmtnDt'] },
  { label: 'Grace Period', keys: ['gracePeriod'] },
  { label: 'Key', keys: ['slnKeyNbr'] },
  { label: 'Status Updated Date', keys: ['authnStatDt'] },
  { label: 'Deactivation Reason', keys: ['stLicDeactvtnRsnDesc'] },
];

const HCP_ISN_COLUMNS = [
  { label: 'Code', keys: ['isnCode'] },
  { label: 'Id', keys: ['Id'] },
  { label: 'Status', keys: ['status'] },
  { label: 'Effective Date', keys: ['effDate'] },
  { label: 'Termination Date', keys: ['termnDate'] },
  { label: 'Deactivation Reason', keys: ['deactvtnRsn'] },
];

const HCPDCR = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, execute } = useAPICall();
  const { execute: executeSecondary } = useAPICall();
  const { execute: executeTimeline } = useAPICall();
  const { loading: loadingHcp, execute: executeHcp } = useAPICall();
  const { loading: loadingStatus, execute: executeStatus } = useAPICall();
  const { loading: loadingCards, execute: executeCards } = useAPICall();
  const reqId = searchParams.get('reqId') || searchParams.get('requestId');
  const mdmId = searchParams.get('mdmId');
  const fromType = searchParams.get('type');
  // const currentUser = useMemo(() => getCurrentUser(), []);

  const [requestDetails, setRequestDetails] = useState(defaultRequestDetails);
  const [rawResponseDetails, setrawResponseDetails] = useState({});
  const [requestorDetails, setRequestorDetails] = useState(defaultRequestorDetails);
  const [hcpDetails, setHcpDetails] = useState(defaultHcpDetails);
  const [baseHcpDetails, setBaseHcpDetails] = useState(defaultHcpDetails);
  const [editedFields, setEditedFields] = useState([]);
  const [hcpTableData, setHcpTableData] = useState({
    srcRecord: [],
    addressDetails: [],
    specialtyDetails: [],
    subSpecialtyDetails: [],
    slnDetails: [],
    isn: [],
  });
  const [facetOptions, setFacetOptions] = useState(defaultFacetOptions);
  const [tabIndex, setTabIndex] = useState(() => (reqId || mdmId ? 1 : 0));
  const [feedbackState, setFeedbackState] = useState({
    open: false,
    evidenceText: '',
    recommendationText: '',
    evidenceResult: '',
    recommendationResult: ''
  });
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  const submitFeedbackApi = async (payload) => {
    try {
      const rawUser = sessionStorage.getItem('user') || localStorage.getItem('user');
      let loggedInUser = requestorDetails?.userId || requestorDetails?.firstName || 'jane.doe';
      if (rawUser) {
        try {
          const parsed = JSON.parse(rawUser);
          loggedInUser = parsed.username || parsed.userName || parsed.name || parsed.userId || loggedInUser;
        } catch (e) {
          loggedInUser = rawUser;
        }
      }

      const response = await apiClient.post(API_CONFIG.dcr.submitLlmFeedback.endpoint, {
        area: 'DCR',
        workflow_name: requestDetails?.recordType?.toLowerCase() === 'address' ? 'address-change' : 'account-change',
        username: loggedInUser,
        evidence_details: payload.evidenceText,
        recommendation_details: payload.recommendationText,
        evidence_result: payload.evidenceResult,
        recommendation_result: payload.recommendationResult,
        feedback_type: null,
        status: 'open',
        request_id: reqId || '',
        thread_id: null,
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  };
  const [paginationByTable, setPaginationByTable] = useState({
    src: { page: 1, pageSize: 5 },
    addr: { page: 1, pageSize: 5 },
    spc: { page: 1, pageSize: 5 },
    sln: { page: 1, pageSize: 5 },
    isn: { page: 1, pageSize: 5 },
  });
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [escalationModalOpen, setEscalationModalOpen] = useState(false);
  const [escalationRecipients, setEscalationRecipients] = useState(DEFAULT_ESCALATION_RECIPIENTS);
  const [escalationToEmail, setEscalationToEmail] = useState(DEFAULT_ESCALATION_RECIPIENTS[0].email);
  const [escalationToName, setEscalationToName] = useState(DEFAULT_ESCALATION_RECIPIENTS[0].name);
  const [ccEmails, setCcEmails] = useState([]);
  const [escalationSubject, setEscalationSubject] = useState('');
  const [escalationBody, setEscalationBody] = useState('');
  const [escalationComments, setEscalationComments] = useState('');
  const [escalationLoading, setEscalationLoading] = useState(false);
  const [escalationSending, setEscalationSending] = useState(false);
  const escalationBodyRef = useRef(null);
  const [selectedAddressRowId, setSelectedAddressRowId] = useState('');
  const [selectedAddressRow, setSelectedAddressRow] = useState(null);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [confirmModal, setConfirmModal] = useState(EMPTY_CONFIRM_MODAL);
  const [agentResponse, setAgentResponse] = useState(null);
  const [cardsDetails, setCardsDetails] = useState(null);
  const [overallStatus, setOverallStatus] = useState(null);
  const [requestLoadFailed, setRequestLoadFailed] = useState(false);
  const [slaCountdown, setSlaCountdown] = useState('');
  const [accountInfoOption, setAccountInfoOption] = useState('remove');
  const [isPageLoading, setIsPageLoading] = useState(Boolean(reqId || mdmId || fromType));
  const [hasRefetchedStatus, setHasRefetchedStatus] = useState(false);
  const { registerPayloadGenerator, clearPayloadGenerator } = useAIAssistantContext() || {};
  const location = useLocation();

  useEffect(() => {
    const isPrescriptionAnalysisCompleted = workflowSteps?.some((step) => {
      const stepKey = String(step?.step || step?.stepName || step?.eventName || step?.name || step?.title || '').toLowerCase();
      const status = String(step?.status || '').toLowerCase();
      return stepKey.includes('prescription data analysis') && status === 'completed';
    });

    if (isPrescriptionAnalysisCompleted && !hasRefetchedStatus && reqId) {
      setHasRefetchedStatus(true);
      executeStatus(API_CONFIG.inbox.getStatusData, null, {
        params: { requestId: reqId },
        showErrorToast: false,
        errorMessage: 'Unable to load persisted workflow data.',
      }).then((data) => {
        if (data) {
          const agenticWorkflow =
            data?.agenticWorkflowStatuses?.[0] ||
            data?.agentStatuses?.find(
              (step) => step?.step === 'agentic_workflow' || step?.stepName === 'agentic_workflow'
            );
          const parsedAgenticMessage = parseWorkflowMessage(agenticWorkflow?.description);
          setAgentResponse(parsedAgenticMessage || agenticWorkflow || null);
        }
      }).catch(() => {});
    }
  }, [workflowSteps, hasRefetchedStatus, reqId, executeStatus]);

  useEffect(() => {
    if (registerPayloadGenerator) {
      registerPayloadGenerator(async () => {
        return {
          screen: "DCR Request",
          dcr_id: requestDetails?.requestId || reqId,
          dcr_type: requestDetails?.recordType?.toLowerCase() || 'account',
          page_context: {
            current_record: rawResponseDetails?.current_record || baseHcpDetails,
            request_record: rawResponseDetails?.request_record || requestDetails,
            workflow_status: workflowSteps.length > 0 ? workflowSteps : [],
            agent_response: agentResponse || {},
          },
        };
      });
    }
    return () => {
      if (clearPayloadGenerator) clearPayloadGenerator();
    };
  }, [
    registerPayloadGenerator,
    clearPayloadGenerator,
    baseHcpDetails,
    requestDetails,
    reqId,
    mdmId,
    tabIndex,
    workflowSteps,
    agentResponse,
    location.pathname,
    rawResponseDetails,
  ]);

  const requestTypeOptions = useMemo(
    () => (requestDetails.recordType === 'Address' ? ['Edit'] : ['Edit']),
    [requestDetails.recordType]
  );

  const recordTypeOptions = useMemo(() => ['Address', 'Account'], []);
  const isReviewMode = Boolean(reqId);
  const shouldShowProfileSection = true;
  const isHcpDetailsReadOnly = isReviewMode || requestDetails?.recordType === 'Address';
  const areRequestFieldsReadOnly = isReviewMode;
  const areAddressDetailsReadOnly = isReviewMode;
  const requestSource = requestDetails?.source || hcpDetails?.source || '';
  const isAddressTableActionDisabled =
    isReviewMode ||
    requestDetails?.recordType === 'Account' ||
    requestSource === 'VEEVA' ||
    requestSource === 'MDMAUTO';
  const selectedRegionalManager = useMemo(
    () => escalationRecipients.find((item) => item.email === escalationToEmail) || escalationRecipients[0] || DEFAULT_ESCALATION_RECIPIENTS[0],
    [escalationRecipients, escalationToEmail]
  );
  useEffect(() => {
    const editor = escalationBodyRef.current;
    if (!editor) return;

    if (escalationLoading) return;

    const nextHtml = escalationBody || '<p>Generated email body will appear here.</p>';
    if (editor.innerHTML !== nextHtml) {
      editor.innerHTML = nextHtml;
    }
  }, [escalationBody, escalationLoading]);

  const updateEscalationBodyFromEditor = useCallback(() => {
    const editor = escalationBodyRef.current;
    if (!editor) return;
    setEscalationBody(editor.innerHTML);
  }, []);

  const applyEscalationFormat = useCallback((command, value = null) => {
    const editor = escalationBodyRef.current;
    if (!editor || escalationLoading) return;
    editor.focus();
    document.execCommand(command, false, value);
    updateEscalationBodyFromEditor();
  }, [escalationLoading, updateEscalationBodyFromEditor]);

  const buildEscalationRequestPayload = useCallback(() => {
    const requestId = String(requestDetails.requestId || reqId || '').trim();
    return {
      requestId,
      currentTerritory:
        firstPresentValue(
          agentResponse?.oldterritoryName,
          agentResponse?.oldterritory,
          baseHcpDetails?.territory,
          baseHcpDetails?.primaryTerritory,
          selectedAddressRow?.currentTerritory
        ) || '',
      proposedTerritory:
        firstPresentValue(
          agentResponse?.newterritoryName,
          agentResponse?.newterritory,
          hcpDetails?.territory,
          hcpDetails?.primaryTerritory,
          selectedAddressRow?.proposedTerritory
        ) || '',
      currentSalesRepresentative:
        firstPresentValue(
          agentResponse?.oldrepName,
          agentResponse?.oldrep,
          requestorDetails?.name
        ) || '',
      proposedSalesRepresentative:
        firstPresentValue(
          agentResponse?.newrepName,
          agentResponse?.newrep,
          selectedAddressRow?.ownerName,
          requestorDetails?.name
        ) || '',
      regionalManagerName: cardsDetails?.regionalManager?.name,
      regionalManagerEmail: cardsDetails?.regionalManager?.email,
      currentTrx:
        firstPresentValue(
          agentResponse?.old_trx,
          agentResponse?.oldTrx
        ) ?? '',
      proposedTrx:
        firstPresentValue(
          agentResponse?.new_trx,
          agentResponse?.newTrx
        ) ?? '',
      // currentRep:
      //   firstPresentValue(
      //     agentResponse?.oldrepName,
      //     agentResponse?.oldrep,
      //     requestorDetails?.name
      //   ) || '',
      // proposedRep:
      //   firstPresentValue(
      //     agentResponse?.newrepName,
      //     agentResponse?.newrep,
      //     selectedAddressRow?.ownerName,
      //     requestorDetails?.name
      //   ) || '',
    };
  }, [
    agentResponse,
    baseHcpDetails?.primaryTerritory,
    baseHcpDetails?.territory,
    hcpDetails?.primaryTerritory,
    hcpDetails?.territory,
    requestDetails.requestId,
    reqId,
    requestorDetails?.name,
    selectedAddressRow?.currentTerritory,
    selectedAddressRow?.ownerName,
    selectedAddressRow?.proposedTerritory,
    escalationToName,
    escalationToEmail,
    selectedRegionalManager?.name,
    selectedRegionalManager?.email,
  ]);

  const generateEscalationDraft = useCallback(async () => {
    const requestId = String(requestDetails.requestId || reqId || '').trim();
    if (!requestId) {
      return;
    }

    try {
      setEscalationLoading(true);
      const response = await executeSecondary(
        API_CONFIG.workflow.generateEscalationEmail,
        buildEscalationRequestPayload(),
        {
          successMessage: '',
          showSuccessToast: false,
          showErrorToast: false,
          errorMessage: 'Unable to generate escalation email.',
        }
      );

      const apiRM = cardsDetails?.regionalManager;
      const responseRM = response?.to ? { name: apiRM?.name || 'Regional Manager', email: response.to } : null;
      const defaultRM = apiRM?.email ? { name: apiRM.name || 'Regional Manager', email: apiRM.email } : null;

      const generatedRecipients = [
        ...(defaultRM ? [defaultRM] : []),
        ...(responseRM ? [responseRM] : []),
        ...DEFAULT_ESCALATION_RECIPIENTS,
      ].filter((item, index, list) => index === list.findIndex((candidate) => candidate.email === item.email));

      setEscalationRecipients(generatedRecipients);
      setEscalationToEmail(apiRM?.email || response?.to || generatedRecipients[0]?.email || DEFAULT_ESCALATION_RECIPIENTS[0].email);
      setEscalationToName(apiRM?.name || (response?.to ? 'Regional Manager' : (generatedRecipients[0]?.name || DEFAULT_ESCALATION_RECIPIENTS[0].name)));
      setEscalationSubject(
        response?.subject ||
        `Approval Required - Territory Ownership Conflict (DCR #${requestId})`
      );
      const rawBody = response?.body || '';
      const formattedBody = rawBody.includes('<br') || rawBody.includes('<p')
        ? rawBody
        : rawBody.replace(/\r?\n/g, '<br/>');
      setEscalationBody(formattedBody);
      // toast.success('Escalation draft loaded.');
    } catch {
      setEscalationRecipients(DEFAULT_ESCALATION_RECIPIENTS);
      setEscalationToEmail(DEFAULT_ESCALATION_RECIPIENTS[0].email);
      setEscalationToName(DEFAULT_ESCALATION_RECIPIENTS[0].name);
      setEscalationSubject(`Approval Required - Territory Ownership Conflict (DCR #${requestId || '-'})`);
      setEscalationBody('');
      toast.error('Unable to generate escalation email.');
    } finally {
      setEscalationLoading(false);
    }
  }, [buildEscalationRequestPayload, executeSecondary, requestDetails.requestId, reqId, cardsDetails]);

  const handleEscalationSend = useCallback(async () => {
    const requestId = String(requestDetails.requestId || reqId || '').trim();
    if (!requestId) {
      toast.error('Request ID is required.');
      return;
    }

    if (!escalationToEmail) {
      toast.error('Please select a recipient.');
      return;
    }

    if (!isValidEmail(escalationToEmail)) {
      toast.error('Please enter a valid "To" email address.');
      return;
    }

    const invalidCcEmails = ccEmails.filter((email) => !isValidEmail(email));
    if (invalidCcEmails.length > 0) {
      toast.error(`Invalid CC email address(es): ${invalidCcEmails.join(', ')}`);
      return;
    }

    const payload = {
      requestId,
      to: escalationToEmail,
      cc: ccEmails,
      subject: escalationSubject,
      body: appendCommentsToBody(escalationBody, escalationComments),
    };

    try {
      setEscalationSending(true);
      await executeSecondary(API_CONFIG.workflow.sendEscalationEmail, payload, {
        successMessage: 'Regional Manager escalation email sent successfully.',
        showSuccessToast: true,
        errorMessage: 'Unable to send escalation email.',
      });
      setEscalationModalOpen(false);
    } catch {
    } finally {
      setEscalationSending(false);
    }
  }, [
    ccEmails,
    escalationBody,
    escalationComments,
    escalationSubject,
    escalationToEmail,
    executeSecondary,
    reqId,
    requestDetails.requestId,
  ]);
  const isEditAddressDisabled =
    isAddressTableActionDisabled ||
    (selectedAddressRow &&
      selectedAddressRow.source &&
      !selectedAddressRow.source.split(',').includes('MDM'));
  const escalationActionLabel = escalationSending
    ? 'Sending...'
    : escalationLoading
      ? 'Generating...'
      : 'Send to Regional Manager';
  const specialtyOptions = useMemo(() => {
    const values = [...facetOptions.specialities, hcpDetails.primarySpecialty]
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);
    return [...new Set(values)];
  }, [facetOptions.specialities, hcpDetails.primarySpecialty]);

  const professionOptions = useMemo(() => {
    const values = [...facetOptions.professions, hcpDetails.primaryProfession]
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);
    return [...new Set(values)];
  }, [facetOptions.professions, hcpDetails.primaryProfession]);

  const statusOptions = useMemo(() => {
    const values = [...facetOptions.status, hcpDetails.status]
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);
    return [...new Set(values)];
  }, [facetOptions.status, hcpDetails.status]);

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const response = await apiClient.get(API_CONFIG.auth.getUserInfo.endpoint);
        const user = response?.data || {};
        setRequestorDetails((prev) => ({
          ...prev,
          name: prev.name || user.name || '',
          email: prev.email || user.email || user.mail || '',
          empNumber: prev.empNumber || user.empNumber || '',
          profName: prev.profName || user.profileName || '',
        }));
      } catch { }
    };

    loadUserInfo();
  }, []);

  useEffect(() => {
    const loadFacetOptions = async () => {
      try {
        const response = await apiClient.post(API_CONFIG.hcp.searchFacet.endpoint, {
          q: 'q=*:* AND -is_deleted:true',
        });
        const fields = response?.data?.facet_counts?.facet_fields || {};
        const toValues = (arr) => (arr || []).filter((_, index) => index % 2 === 0);

        setFacetOptions({
          professions: toValues(fields.professions),
          specialities: toValues(fields.specialities),
          status: toValues(fields.status),
          state: toValues(fields.state),
        });
      } catch {
        setFacetOptions(defaultFacetOptions);
      }
    };

    loadFacetOptions();
  }, []);
  const getInputPlaceholder = (label) => `Enter ${label}`;

  useEffect(() => {
    const loadLinkedHcpDetails = async () => {
      const linkedMdmId = requestDetails.accountId || hcpDetails.mdmId || mdmId;
      if (!linkedMdmId) return;

      try {
        const response = await executeHcp(API_CONFIG.workflow.getHcpByMdmId, null, {
          params: { mdmId: linkedMdmId },
          showErrorToast: false,
          errorMessage: 'Unable to load HCP related tables.',
        });
        setrawResponseDetails(prev => ({ ...prev, current_record: response }));
        const account = response?.data || response || {};
        setSelectedAddressRowId('');
        setSelectedAddressRow(null);
        setHcpTableData({
          srcRecord: formatTableData(account.srcRecord, 'src'),
          addressDetails: formatTableData(
            account.addressDetails,
            'addr',
            account.overriddenFields || []
          ),
          specialtyDetails: formatTableData(account.specialtyDetails, 'spc'),
          subSpecialtyDetails: formatTableData(account.subSpecialtyDetails, 'subspec'),
          slnDetails: formatTableData(account.slnDetails, 'sln'),
          isn: formatTableData(account.isn, 'isn'),
        });

        const mappedHcp = {
          firstName: account.firstName || '',
          middleName: account.middleName || '',
          lastName: account.lastName || '',
          suffix: account.suffix || '',
          gender: account.gender || '',
          birthYr: account.birthYr || '',
          primarySpecialty: account.primarySpecialty || '',
          primaryProfession: account.primaryProfession || '',
          status: account.status || '',
          okIndvId: account.okIndvId || '',
          npiId: account.npiId || '',
          imsId: account.imsId || '',
          amaId: account.amaId || '',
          slnId: account.slnId || '',
          isGoldenUniverse: account.isGoldenUniverse || '',
          mdmId: account.mdmId || linkedMdmId,
          source: account.source || '',
          primaryAddress: {
            addrLn1: account.primaryAddress?.addrLn1 || '',
            addrLn2: account.primaryAddress?.addrLn2 || '',
            cityNm: account.primaryAddress?.cityNm || '',
            stCd: account.primaryAddress?.stCd || '',
            zipCd: [
              account.primaryAddress?.zipCd?.[0] || '',
              account.primaryAddress?.zipCd?.[1] || '',
            ],
          },
        };

        setBaseHcpDetails((prev) => {
          const newBase = { ...mappedHcp, addrDcrObj: prev.addrDcrObj };
          setEditedFields((currentEditedFields) => {
            setHcpDetails(applyEditedFields(newBase, currentEditedFields));
            return currentEditedFields;
          });
          return newBase;
        });
      } catch { 
      } finally {
        setIsPageLoading(false);
      }
    };
    const getStatusData = async () => {
      if (!reqId) return null;

      try {
        return await executeStatus(API_CONFIG.inbox.getStatusData, null, {
          params: { requestId: reqId },
          showErrorToast: false,
          errorMessage: 'Unable to load persisted workflow data.',
        });
      } catch {
        return null;
      }
    };

    if (reqId || mdmId) {
      loadLinkedHcpDetails();
    }
    if (reqId) {
      getStatusData().then((data) => {
        if (data) {
          const agenticWorkflow =
            data?.agenticWorkflowStatuses?.[0] ||
            data?.agentStatuses?.find(
              (step) => step?.step === 'agentic_workflow' || step?.stepName === 'agentic_workflow'
            );
          const parsedAgenticMessage = parseWorkflowMessage(agenticWorkflow?.description);
          setAgentResponse(parsedAgenticMessage || agenticWorkflow || null);
        }
      });
      }
    }, [executeSecondary, hcpDetails.mdmId, mdmId, reqId, requestDetails.accountId]);

    const isWorkflowCompleted = useMemo(() => {
      return (
        workflowSteps?.length > 0 &&
        (
          areAllTimelineStepsCompleted(workflowSteps) ||
          workflowSteps.some(
            (step) => step.status === 'human_review_required'
          )
        )
      );
    }, [workflowSteps]);

    useEffect(() => {
      if (!reqId) return;
  
      executeCards(API_CONFIG.dcr.getCardsDetails, { requestId: reqId }, {
        showErrorToast: false,
      }).then(res => {
        if (res) {
          setCardsDetails(res.data || res);
        }
      }).catch(err => {
        console.error("Error fetching cards details", err);
      });
    }, [reqId, isWorkflowCompleted, executeCards]);

  useEffect(() => {
    if (!escalationModalOpen) return;
    generateEscalationDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escalationModalOpen]);

  const updateHcpField = (field, value) => {
    setHcpDetails((prev) => ({ ...prev, [field]: value }));
  };

  const updateAddressDcrField = useCallback((field, value) => {
    setHcpDetails((prev) => ({
      ...prev,
      addrDcrObj: {
        ...prev.addrDcrObj,
        [field]: value,
      },
    }));
  }, []);

  const updateAddressZipField = useCallback((index, value) => {
    setHcpDetails((prev) => {
      const nextZip = normalizeZipCode(prev.addrDcrObj?.zipCd);
      nextZip[index] = value;

      return {
        ...prev,
        addrDcrObj: {
          ...prev.addrDcrObj,
          zipCd: nextZip,
        },
      };
    });
  }, []);

  const updateEditedField = useCallback((index, field, value) => {
    setEditedFields((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
    );
  }, []);

  const hydrateDcrResponse = useCallback((rawResponse) => {
    setrawResponseDetails(prev => ({ ...prev, request_record: rawResponse, }));
    const mapped = mapHcpResponse(rawResponse);
    const hasData = Boolean(
      mapped?.requestDetails?.requestId ||
      mapped?.requestDetails?.accountId ||
      mapped?.hcpDetails?.mdmId
    );
    if (!hasData) return false;

    setRequestDetails(mapped.requestDetails);
    setRequestorDetails((prev) => ({ ...prev, ...mapped.requestorDetails }));
    setHcpDetails(mapped.hcpDetails);
    setBaseHcpDetails(mapped.baseHcpDetails);
    setEditedFields(mapped.editedFields);
    setSelectedAddressRowId('');
    setSelectedAddressRow(null);
    setHcpTableData({
      srcRecord: mapped.tableData.srcRecord,
      addressDetails: mapped.tableData.address,
      specialtyDetails: mapped.tableData.specialty,
      subSpecialtyDetails: mapped.tableData.subSpecialty || [],
      slnDetails: mapped.tableData.sln,
      isn: mapped.tableData.isn,
    });
    return { hydrated: true, accountId: mapped?.requestDetails?.accountId || mapped?.hcpDetails?.mdmId };
  }, []);

  const reloadDcrByRequestId = useCallback(
    async (requestId) => {
      if (!requestId) return false;

      let hydrated = false;
      try {
        const response = await execute(
          API_CONFIG.workflow.getOpenDcrByRequestId,
          { _ts: Date.now() },
          {
            params: { reqId: requestId },
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              Pragma: 'no-cache',
              Expires: '0',
            },
            showErrorToast: false,
            errorMessage: 'Unable to load HCP DCR.',
          }
        );
        hydrated = hydrateDcrResponse(response);
      } catch { }

      if (!hydrated) {
        try {
          const fallbackUrl = `/getOpenDcr/${encodeURIComponent(
            requestId
          )}?_ts=${Date.now()}&_cb=${Math.random().toString(36).slice(2)}`;
          const fallbackResponse = await fetch(fallbackUrl, {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store',
            headers: {
              Accept: 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              Pragma: 'no-cache',
              Expires: '0',
            },
          });

          if (fallbackResponse.ok) {
            const fallbackJson = await fallbackResponse.json();
            hydrated = hydrateDcrResponse(fallbackJson);
          }
        } catch { }
      }

      return hydrated;
    },
    [execute, hydrateDcrResponse]
  );

  useEffect(() => {
    const init = async () => {
      if (fromType) {
        const sessionData = sessionStorage.getItem('hcpGlobalSearchDcrData');
        if (!sessionData) {
          setIsPageLoading(false);
          return;
        }
        try {
          const parsed = JSON.parse(sessionData);
          setRequestDetails((prev) => ({ ...prev, requestType: 'New', recordType: 'Account' }));
          setHcpDetails((prev) => ({
            ...prev,
            firstName: parsed.firstName || '',
            middleName: parsed.middleName || '',
            lastName: parsed.lastName || '',
            suffix: parsed.suffix || '',
            gender: parsed.gender || '',
            birthYr: parsed.birthYr || '',
            source: parsed.source || '',
            primarySpecialty: parsed.primarySpecialty || '',
            primaryProfession: parsed.primaryProfession || '',
            status: parsed.status || '',
            okIndvId: parsed.okIndvId || '',
            npiId: parsed.npiId || '',
            primaryAddress: {
              addrLn1: parsed.primaryAddress?.addrLn1 || '',
              addrLn2: parsed.primaryAddress?.addrLn2 || '',
              cityNm: parsed.primaryAddress?.cityNm || '',
              stCd: parsed.primaryAddress?.stCd || '',
              zipCd: [
                parsed.primaryAddress?.zipCd?.[0] || '',
                parsed.primaryAddress?.zipCd?.[1] || '',
              ],
            },
            addrDcrObj: buildAddressDcrObject(parsed.primaryAddress),
          }));
        } catch { }
        setIsPageLoading(false);
        return;
      }

      if (reqId) {
        setRequestLoadFailed(false);
        const result = await reloadDcrByRequestId(reqId);
        if (!result || !result.accountId) {
          setRequestLoadFailed(true);
          setIsPageLoading(false);
        } else {
          setIsPageLoading(false);
        }
        return;
      }

      if (mdmId) {
        try {
          const response = await execute(API_CONFIG.workflow.getHcpByMdmId, null, {
            params: { mdmId },
            showErrorToast: false,
            errorMessage: 'Unable to load HCP details.',
          });
          setrawResponseDetails(prev => ({ ...prev, current_record: response }));
          const account = response?.data || response || {};
          setRequestDetails((prev) => ({
            ...prev,
            requestType: account?.requestType || 'Edit',
            recordType: account?.recordType || 'Account',
            accountId: account.mdmId || mdmId,
            accountName: [account.firstName, account.lastName].filter(Boolean).join(' '),
          }));
          const mappedHcp = {
            firstName: account.firstName || '',
            middleName: account.middleName || '',
            lastName: account.lastName || '',
            suffix: account.suffix || '',
            gender: account.gender || '',
            birthYr: account.birthYr || '',
            primarySpecialty: account.primarySpecialty || '',
            primaryProfession: account.primaryProfession || '',
            status: account.status || '',
            okIndvId: account.okIndvId || '',
            npiId: account.npiId || '',
            imsId: account.imsId || '',
            amaId: account.amaId || '',
            slnId: account.slnId || '',
            isGoldenUniverse: account.isGoldenUniverse || '',
            mdmId: account.mdmId || mdmId,
            source: account.source || '',
            primaryAddress: {
              addrLn1: account.primaryAddress?.addrLn1 || '',
              addrLn2: account.primaryAddress?.addrLn2 || '',
              cityNm: account.primaryAddress?.cityNm || '',
              stCd: account.primaryAddress?.stCd || '',
              zipCd: [
                account.primaryAddress?.zipCd?.[0] || '',
                account.primaryAddress?.zipCd?.[1] || '',
              ],
            },
            addrDcrObj: createInitialAddressDcrState(),
          };
          setHcpDetails(mappedHcp);
          setBaseHcpDetails(mappedHcp);
          setSelectedAddressRowId('');
          setSelectedAddressRow(null);
          setHcpTableData({
            srcRecord: formatTableData(account.srcRecord, 'src'),
            addressDetails: formatTableData(
              account.addressDetails,
              'addr',
              account.overriddenFields || []
            ),
            specialtyDetails: formatTableData(account.specialtyDetails, 'spc'),
            subSpecialtyDetails: formatTableData(account.subSpecialtyDetails, 'subspec'),
            slnDetails: formatTableData(account.slnDetails, 'sln'),
            isn: formatTableData(account.isnDetails, 'isn'),
          });
          setRequestLoadFailed(false);
        } catch { }
        setIsPageLoading(false);
      }
    };

    init();
  }, [execute, fromType, mdmId, reloadDcrByRequestId, reqId]);

  const getTableRowId = useCallback(
    (row, keyPrefix, index) =>
      `${keyPrefix}-${row.addrId || row.srname || row.slnAuthNbr || row.Id || row.rank || index}`,
    []
  );

  const handleAddressRowSelect = useCallback(
    (row) => {
      if (!row) return;
      const rowIndex = hcpTableData.addressDetails.findIndex(
        (item) => item?.addrId === row?.addrId
      );
      setSelectedAddressRow(row);
      setSelectedAddressRowId(getTableRowId(row, 'addr', rowIndex));
    },
    [getTableRowId, hcpTableData.addressDetails]
  );

  const handleAddressToolbarAction = useCallback(
    (action) => {
      if (!selectedAddressRow) {
        toast.error('Please select the Address!');
        return;
      }

      const currentEditedAddressId = hcpDetails?.addrDcrObj?.addrId || '';
      if (currentEditedAddressId && currentEditedAddressId !== selectedAddressRow.addrId) {
        toast.error(
          'Already one address is edited. You can make further changes on same address. To edit another address, create a new DCR'
        );
        return;
      }

      if (action === 'edit' && selectedAddressRow?.source !== 'MDM') {
        return;
      }

      if (action === 'masterPrimary' && selectedAddressRow?.masterPrimary === true) {
        toast.error('Selected Address was already a Primary Address. Please select new one.');
        return;
      }

      const nextAddress = buildAddressDcrObject(selectedAddressRow);
      nextAddress.addrId = selectedAddressRow?.addrId || '';

      if (action === 'masterPrimary') {
        nextAddress.masterPrimary = 'true';
      }

      setHcpDetails((prev) => ({
        ...prev,
        addrDcrObj: nextAddress,
      }));
      setBaseHcpDetails((prev) => ({
        ...prev,
        addrDcrObj: buildAddressDcrObject(selectedAddressRow),
      }));
    },
    [hcpDetails?.addrDcrObj?.addrId, selectedAddressRow]
  );

  const handleStatusChange = useCallback((index, nextStatus) => {
    const normalizedStatus = nextStatus === 'Rejected' ? 'Rejected' : 'Accepted';
    setEditedFields((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, mdmStatus: normalizedStatus } : item))
    );
  }, []);

  const tabTableConfigs = useMemo(
    () => [
      {
        label: 'Source Records',
        columns: HCP_SOURCE_COLUMNS,
        rows: hcpTableData.srcRecord,
        emptyMessage: 'No source records found',
        keyPrefix: 'src',
        tableKey: 'src',
      },
      {
        label: 'Address',
        columns: HCP_ADDRESS_COLUMNS,
        rows: sortMasterPrimaryFirst(hcpTableData.addressDetails),
        emptyMessage: 'No address records found',
        keyPrefix: 'addr',
        tableKey: 'addr',
      },
      {
        label: 'Specialty',
        columns: HCP_SPECIALTY_COLUMNS,
        rows: hcpTableData.specialtyDetails,
        emptyMessage: 'No specialty records found',
        keyPrefix: 'spc',
        tableKey: 'spc',
      },
      {
        label: 'SLN',
        columns: HCP_SLN_COLUMNS,
        rows: hcpTableData.slnDetails,
        emptyMessage: 'No SLN records found',
        keyPrefix: 'sln',
        tableKey: 'sln',
      },
      {
        label: 'ISN',
        columns: HCP_ISN_COLUMNS,
        rows: hcpTableData.isn,
        emptyMessage: 'No ISN records found',
        keyPrefix: 'isn',
        tableKey: 'isn',
      },
    ],
    [hcpTableData]
  );

  const activeTabConfig = tabTableConfigs[tabIndex] || tabTableConfigs[0];

  const tabTableInitialState = useMemo(
    () => ({
      showGlobalFilter: false,
      showColumnFilters: false,
    }),
    []
  );

  const setTabPage = useCallback((tableKey, nextPage) => {
    setPaginationByTable((prev) => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        page: nextPage,
      },
    }));
  }, []);

  const setTabPageSize = useCallback((tableKey, nextPageSize) => {
    setPaginationByTable((prev) => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        pageSize: nextPageSize,
        page: prev[tableKey].pageSize === nextPageSize ? prev[tableKey].page : 1,
      },
    }));
  }, []);

  const renderDcrTopToolbar = useCallback(
    ({ table }) => (
      <Box
        sx={{
          pt: 1,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#fff',
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, value) => setTabIndex(value)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{
            style: {
              backgroundColor: '#1976d2',
              height: '2px',
              borderTopLeftRadius: '2px',
              borderTopRightRadius: '2px',
            },
          }}
          sx={{
            minHeight: 44,
            '& .MuiTabs-flexContainer': { gap: 3 },
          }}
        >
          {tabTableConfigs.map((item, index) => (
            <Tab
              key={item.tableKey}
              label={item.label}
              disableRipple
              sx={{
                minHeight: 44,
                minWidth: 'auto',
                px: 1,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                
                color: '#64748b',
                backgroundColor: 'transparent',
                transition: 'color 0.2s',
                opacity: 1,
                '&.Mui-selected': {
                  color: '#1976d2',
                  backgroundColor: 'transparent',
                },
                '&:hover': {
                  color: tabIndex === index ? '#1976d2' : '#0f172a',
                  backgroundColor: 'transparent',
                },
              }}
            />
          ))}
        </Tabs>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1.5,
            pr: 0,
            py: 0.5,
          }}
        >
          {activeTabConfig?.tableKey === 'addr' && Boolean(mdmId) ? (
            <>
              <Box sx={{ minWidth: table.getState().showGlobalFilter ? 260 : 0, maxWidth: 340 }}>
                {table.getState().showGlobalFilter ? <MrtGlobalFilterTextField table={table} /> : null}
              </Box>
              <MrtToggleGlobalFilterButton table={table} />
              <MrtToggleFiltersButton table={table} />
              <MrtShowHideColumnsButton table={table} />
              <MrtToggleDensePaddingButton table={table} />
              <MrtFullScreenToggleButton table={table} />
              <Button className="action-btn process-btn action-btn process-btn"
                size="small"
                variant="outlined"
                disabled={isAddressTableActionDisabled}
                onClick={() => handleAddressToolbarAction('masterPrimary')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                   height: 33,
                  borderRadius: '4px',
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&.Mui-disabled': { borderColor: '#e2e8f0', backgroundColor: '#f8fafc', color: '#94a3b8' }
                }}
              >
                Set Master Primary
              </Button>
              <Button className="action-btn process-btn action-btn process-btn"
                size="small"
                variant="outlined"
                disabled={isEditAddressDisabled}
                onClick={() => handleAddressToolbarAction('edit')}
                sx={{
                   height: 33,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '4px',
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&.Mui-disabled': { borderColor: '#e2e8f0', backgroundColor: '#f8fafc', color: '#94a3b8' }
                }}
              >
                Edit
              </Button>
            </>
          ) : null}
        </Box>
      </Box>
    ),
    [
      activeTabConfig?.tableKey,
      handleAddressToolbarAction,
      isAddressTableActionDisabled,
      isEditAddressDisabled,
      mdmId,
      tabIndex,
      tabTableConfigs,
    ]
  );

  const renderTabTable = (columns, rows, emptyMessage, keyPrefix, tableKey) => (
    <GlobalMaterialTable
      columns={[
        ...columns.map((column) => ({
          header: column.label,
          id: `${keyPrefix}-${column.label}`,
          accessorFn: (row) =>
            formatScalar(column.render ? column.render(row) : getTabValue(row, column.keys)),
        })),
      ]}
      data={
        tableKey === 'addr'
          ? sortMasterPrimaryFirst(rows)
          : rows
      }
      emptyMessage={emptyMessage}
      enablePagination={true}
      enableTopToolbar={true}
      enableBottomToolbar={true}
      initialState={{
        ...tabTableInitialState,
      }}
      autoResetPageIndex={false}
      renderTopToolbar={renderDcrTopToolbar}
      page={paginationByTable[tableKey]?.page || 1}
      pageSize={paginationByTable[tableKey]?.pageSize || 5}
      onPageChange={(nextPage) => setTabPage(tableKey, nextPage)}
      onPageSizeChange={(nextPageSize) => setTabPageSize(tableKey, nextPageSize)}
      rowCount={rows.length}
      manualPagination={false}
      muiTablePaginationProps={{
        rowsPerPageOptions: [5, 10, 20],
      }}
      getRowId={(row, index) => getTableRowId(row, keyPrefix, index)}
      muiTablePaperProps={{ elevation: 0, sx: { borderRadius: '8px' } }}
      muiTableProps={{ sx: { tableLayout: 'auto' } }}
      muiTableHeadCellProps={{
        sx: {
          '& .Mui-TableHeadCell-Content-Labels': { overflow: 'visible' },
          '& .MuiTypography-root': { overflow: 'visible', textOverflow: 'clip',  fontWeight: 600, color: '#475569' }
        }
      }}
      muiTableBodyCellProps={{
        sx: {  color: '#1e293b' }
      }}
      defaultColumn={{ size: 10, minSize: 10 }}
      onRowClick={tableKey === 'addr' ? handleAddressRowSelect : undefined}
      selectedRowId={tableKey === 'addr' ? selectedAddressRowId : ''}
      muiTableBodyRowProps={
        tableKey === 'addr'
          ? ({ row }) =>
            isMasterPrimaryAddress(row.original)
              ? {
                sx: {
                  '& td': {
                    backgroundColor: alpha('#f59e0b', 0.12),
                  },
                  '&:hover td': {
                    backgroundColor: alpha('#f59e0b', 0.18),
                  },
                },
              }
              : {}
          : undefined
      }
    />
  );


  const loadTimelineEvents = useCallback(async () => {
    if (!reqId) {
      setWorkflowSteps([]);
      return false;
    }

    try {
      const response = await executeTimeline(
        API_CONFIG.workflow.getAgentStatus,
        {
          request_id: reqId,
        },
        {
          showErrorToast: false,
          errorMessage: 'Unable to load agent workflow status.',
        }
      );

      const workflowStatus = response?.agentStatuses
        ?.filter(item => item.step === "data_steward_agent_workflow")
        ?.sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        )[0];
      if (workflowStatus?.status === 'failed') {
        return true;
      }

      let shouldStopPolling = false;
      if (workflowStatus?.status === 'completed' && workflowStatus?.step === "data_steward_agent_workflow") {
        shouldStopPolling = true;
      }

      const rawOverallStatus =
        response?.detailedStatus ||
        response?.overallStatus ||
        response?.status ||
        workflowStatus?.status ||
        '';
      setOverallStatus(rawOverallStatus);

      const items = extractTimelineItems(response);
      const agentStatusItems = items.some(
        (item) =>
          item?.step ||
          item?.stepName ||
          item?.eventName ||
          item?.request_id ||
          item?.requestId ||
          item?.dcr_id ||
          item?.dcrId
      )
        ? normalizeAgentStatusEvents(items)
        : items;
      const statusItems =
        agentStatusItems.length > 0
          ? agentStatusItems
          : buildTimelineItemsFromStatusObject(response);
      const normalized = normalizeTimelineSteps(statusItems);
      const workflowQuery = getWorkflowQueryText(
        response?.query,
        response?.data?.query,
        response?.result?.query,
        response?.payload?.query,
        response?.agenticWorkflowStatuses?.find((step) =>
          String(step?.step || step?.stepName || step?.name || step?.title || '').toLowerCase().includes('trx')
        )?.query,
        agentResponse?.query,
        agentResponse?.sqlQuery,
        agentResponse?.searchQuery
      );
      const workflowWebsite =
        agentResponse?.website ||
        agentResponse?.data?.website ||
        agentResponse?.payload?.website ||
        response?.website ||
        response?.data?.website ||
        response?.payload?.website;
      const workflowWebsiteInfo =
        agentResponse?.website_info ||
        agentResponse?.wesite_info ||
        agentResponse?.data?.website_info ||
        agentResponse?.data?.wesite_info ||
        response?.website_info ||
        response?.wesite_info ||
        response?.data?.website_info ||
        response?.data?.wesite_info;
      const hasWebsiteEvidence = Boolean(workflowWebsite || workflowWebsiteInfo);
      const enriched = workflowQuery || hasWebsiteEvidence
        ? normalized.map((step) => {
            const stepKey = String(step.step || step.stepName || step.eventName || step.name || step.title || '').toLowerCase();
            if (
              stepKey.includes('prescription') ||
              stepKey.includes('trx') ||
              stepKey.includes('retrieve_trx_data') ||
              stepKey.includes('retrieving prescription') ||
              stepKey.includes('retrieving_prescription')

            ) {
              return {
                ...step,
                query: workflowQuery,
              };
            }
            if (hasWebsiteEvidence && (stepKey.includes('internet_search') || stepKey.includes('internet search'))) {
              return {
                ...step,
                website: workflowWebsite,
                wesite_info: workflowWebsiteInfo,
              };
            }
            return step;
          })
        : normalized;

      if (enriched.length > 0) {
        setWorkflowSteps(enriched);
        return shouldStopPolling || areAllTimelineStepsCompleted(enriched);
      }

      setWorkflowSteps([]);
      return shouldStopPolling;
    } catch (err) {
      // toast.error('API or Network error occurred. Workflow polling stopped.');
      setWorkflowSteps([]);
      return true;
    }
  }, [executeTimeline, reqId, agentResponse]);

  useEffect(() => {
    if (!reqId) {
      setWorkflowSteps([]);
    }
  }, [reqId]);

  useEffect(() => {
    let slaDueBy = null;
    if (workflowSteps && workflowSteps.length > 0) {
      const stepWithSla = workflowSteps.find(s => s.slaDueBy);
      if (stepWithSla) slaDueBy = stepWithSla.slaDueBy;
    }
    if (!slaDueBy) {
      slaDueBy = agentResponse?.slaDueBy || rawResponseDetails?.request_record?.slaDueBy || rawResponseDetails?.slaDueBy;
    }

    if (!slaDueBy) return;

    const calculateSLARemaining = () => {
      const dueTime = new Date(slaDueBy).getTime();
      if (isNaN(dueTime)) return null;
      const now = new Date().getTime();
      const diffMs = dueTime - now;
      if (diffMs <= 0) return '00h 00m';
      const hrs = Math.floor(diffMs / (1000 * 60 * 60));
      const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      // const secs = Math.floor((diffMs % (1000 * 60)) / 1000);
      
      return `${String(hrs).padStart(2, '0')}h ${String(mins).padStart(2, '0')}m`;
      // return `${String(hrs).padStart(2, '0')}h ${String(mins).padStart(2, '0')}m ${String(secs).padStart(2, '0')}s`;
    };

    setSlaCountdown(calculateSLARemaining() || '');
    
    // Using 60000ms for hr:m. Change to 1000ms if testing hr:m:s
    const timer = setInterval(() => {
      setSlaCountdown(calculateSLARemaining() || '');
    }, 60000);
    
    return () => clearInterval(timer);
  }, [workflowSteps, agentResponse, rawResponseDetails]);

  useEffect(() => {
    if (!reqId) return undefined;

    let intervalId;
    let cancelled = false;

    const pollTimeline = async () => {
      const isComplete = await loadTimelineEvents();

      if (isComplete && intervalId) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }

      return isComplete;
    };

    pollTimeline().then((isComplete) => {
      if (!cancelled && !isComplete) {
        intervalId = window.setInterval(pollTimeline, TIMELINE_POLL_INTERVAL_MS);
      }
    });

    return () => {
      cancelled = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [loadTimelineEvents, reqId]);



  const executeClose = async (decision) => {
    setConfirmModal(EMPTY_CONFIRM_MODAL);
    try {
      setClosing(true);
      // decision is passed as payload which for GET requests becomes axios query params (?decision=APPROVED / ?decision=REJECTED)
      await executeSecondary(API_CONFIG.dcr.closeDcr, decision ? { decision } : null, {
        params: { requestId: reqId ?? requestDetails.requestId },
        // headers: currentUser?.username ? { user: currentUser.username } : undefined,
        successMessage: 'HCP request closed successfully.',
        showSuccessToast: true,
        errorMessage: 'Unable to close HCP request.',
      });
      navigate(-1);
    } catch {
    } finally {
      setClosing(false);
    }
  };

  const evaluateEoqAndClose = (decision) => {
    executeClose(decision);
  };

  const initiateClose = (decision) => {
    // const unreviewed = editedFields.some((el) => !el.mdmStatus);
    // if (unreviewed) {
    //   toast.warning(
    //     'Accept or Reject the data change items before updating/saving the edited request'
    //   );
    //   return;
    // }
    //commented out the editable field status in order to close request approved or rejected.

    if (fromType) {
      evaluateEoqAndClose(decision);
      return;
    }

    setConfirmModal({
      open: true,
      message: decision === 'APPROVED'
        ? 'Are you sure you want to approve this request? This action will finalise the changes.'
        : decision === 'REJECTED'
          ? 'Are you sure you want to reject this request? This action cannot be undone.'
          : 'Changes have been made in this request. Do you want to override and update this request?',
      confirmText: decision === 'APPROVED' ? 'Approve' : decision === 'REJECTED' ? 'Reject' : 'Update',
      onConfirm: () => evaluateEoqAndClose(decision),
      isDanger: decision === 'REJECTED' || (!decision),
      isSuccess: decision === 'APPROVED',
    });
  };
  const saveDcr = async () => {
    const requestorEmail = requestorDetails.email?.trim();
    if (!requestorEmail) {
      toast.error('Requestor Email is required.');
      return;
    }

    const requestId = requestDetails.requestId || reqId;
    if (!requestId) {
      toast.error('Request ID is required.');
      return;
    }

    try {
      setSaving(true);
      const response = await executeSecondary(
        API_CONFIG.inbox.createRequest,
        {
          data: buildSavePayload(
            requestDetails,
            requestorDetails,
            hcpDetails,
            baseHcpDetails,
            editedFields
          ),
        },
        {
          params: { requestId },
          // headers: currentUser?.username ? { user: currentUser.username } : undefined,
          successMessage: 'HCP request saved successfully.',
          showSuccessToast: true,
          errorMessage: 'Unable to save HCP request.',
        }
      );
      const savedRequestId = response?.requestId || response?.data?.requestId || requestId;
      navigate('/inbox');
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const handleRequestTypeChange = (event) => {
    const nextType = event.target.value;
    setRequestDetails((prev) => ({
      ...prev,
      requestType: nextType,
      recordType: nextType === 'New' ? 'Address' : prev.recordType,
    }));
    if (nextType === 'New') {
      setHcpDetails((prev) => ({
        ...prev,
        addrDcrObj: createInitialAddressDcrState(),
      }));
      setBaseHcpDetails((prev) => ({
        ...prev,
        addrDcrObj: buildAddressDcrObject(),
      }));
      setEditedFields((prev) =>
        prev.filter(
          (field) =>
            ![
              'addrLn1',
              'addrLn2',
              'cityNm',
              'stCd',
              'zipCd',
              'category',
              'setting',
              'isSampleable',
              'statusLabel',
              'masterPrimary',
            ].includes(field.fieldName)
        )
      );
    }
  };

  const handleRecordTypeChange = (event) => {
    const nextType = event.target.value;
    setRequestDetails((prev) => ({
      ...prev,
      recordType: nextType,
      requestType: nextType === 'Account' ? 'Edit' : prev.requestType,
    }));
    setHcpDetails((prev) => ({
      ...prev,
      addrDcrObj: nextType === 'Address' ? createInitialAddressDcrState() : buildAddressDcrObject(),
    }));
    setBaseHcpDetails((prev) => ({
      ...prev,
      addrDcrObj: nextType === 'Address' ? buildAddressDcrObject() : prev.addrDcrObj,
    }));
  };

  const formatDcrValue = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).join('-') || '-';
    if (value === null || value === undefined || value === '') return '-';
    return value;
  };

  const firstPresentValue = (...values) =>
    values.find((value) => value !== null && value !== undefined && value !== '');

  // For Address Change requests, only show address-related fields (not HCP account info fields).
  // Rep and Territory fields are included (data will be populated once the API is available).
  const incomingAddressFields = [
    {
      label: 'Address Line 1',
      oldValue: agentResponse?.old_addressline1,
      newValue: agentResponse?.new_addressline1,
    },
    {
      label: 'Address Line 2',
      oldValue: agentResponse?.old_addressline2,
      newValue: agentResponse?.new_addressline2,
    },
    {
      label: 'City',
      oldValue: agentResponse?.old_cityNm,
      newValue: agentResponse?.new_cityNm,
    },
    {
      label: 'State',
      oldValue: agentResponse?.old_stcd || agentResponse?.old_stdcd,
      newValue: agentResponse?.new_stcd || agentResponse?.new_stdcd,
    },
    {
      label: 'Zip',
      oldValue: agentResponse?.old_zip,
      newValue: agentResponse?.new_zip,
    },
    {
      label: 'Country',
      oldValue: firstPresentValue(agentResponse?.oldcountry, agentResponse?.oldcountryCd),
      newValue: firstPresentValue(agentResponse?.newcountry, agentResponse?.newcountryCd),
    },
    {
      label: 'Rep',
      oldValue: firstPresentValue(agentResponse?.oldrep, agentResponse?.oldrepName),
      newValue: firstPresentValue(agentResponse?.newrep, agentResponse?.newrepName),
    },
    {
      label: 'Territory',
      oldValue: firstPresentValue(agentResponse?.oldterritory, agentResponse?.oldterritoryName),
      newValue: firstPresentValue(agentResponse?.newterritory, agentResponse?.newterritoryName),
    },
    {
      label: 'Rank',
      oldValue: firstPresentValue(agentResponse?.old_rank, agentResponse?.rank, baseHcpDetails?.primaryAddress?.rank),
      newValue: firstPresentValue(agentResponse?.new_rank, agentResponse?.rank, hcpDetails?.primaryAddress?.rank),
    },
  ];

  const accountFields = [
    {
      label: 'First Name',
      oldValue: firstPresentValue(agentResponse?.old_firstName, agentResponse?.old_first_name, agentResponse?.old_firstname, baseHcpDetails?.firstName, baseHcpDetails?.fName, baseHcpDetails?.first_name, baseHcpDetails?.firstname),
      newValue: firstPresentValue(agentResponse?.new_firstName, agentResponse?.new_first_name, agentResponse?.new_firstname, hcpDetails?.firstName, hcpDetails?.fName, hcpDetails?.first_name, hcpDetails?.firstname),
    },
    {
      label: 'Middle Name',
      oldValue: firstPresentValue(agentResponse?.old_middleName, agentResponse?.old_middle_name, agentResponse?.old_middlename, baseHcpDetails?.middleName, baseHcpDetails?.mName, baseHcpDetails?.middle_name, baseHcpDetails?.middlename),
      newValue: firstPresentValue(agentResponse?.new_middleName, agentResponse?.new_middle_name, agentResponse?.new_middlename, hcpDetails?.middleName, hcpDetails?.mName, hcpDetails?.middle_name, hcpDetails?.middlename),
    },
    {
      label: 'Last Name',
      oldValue: firstPresentValue(agentResponse?.old_lastName, agentResponse?.old_last_name, agentResponse?.old_lastname, baseHcpDetails?.lastName, baseHcpDetails?.lName, baseHcpDetails?.last_name, baseHcpDetails?.lastname),
      newValue: firstPresentValue(agentResponse?.new_lastName, agentResponse?.new_last_name, agentResponse?.new_lastname, hcpDetails?.lastName, hcpDetails?.lName, hcpDetails?.last_name, hcpDetails?.lastname),
    },
    {
      label: 'Profession',
      oldValue: firstPresentValue(agentResponse?.old_primaryProfession, agentResponse?.old_primary_profession, baseHcpDetails?.primaryProfession),
      newValue: firstPresentValue(agentResponse?.new_primaryProfession, agentResponse?.new_primary_profession, hcpDetails?.primaryProfession),
    },
    {
      label: 'Speciality',
      oldValue: firstPresentValue(agentResponse?.old_primarySpecialty, agentResponse?.old_primary_specialty, baseHcpDetails?.primarySpecialty),
      newValue: firstPresentValue(agentResponse?.new_primarySpecialty, agentResponse?.new_primary_specialty, hcpDetails?.primarySpecialty),
    },
    {
      label: 'NPI ID',
      oldValue: firstPresentValue(agentResponse?.old_npiId, agentResponse?.old_npi_id, baseHcpDetails?.npiId),
      newValue: firstPresentValue(agentResponse?.new_npiId, agentResponse?.new_npi_id, hcpDetails?.npiId),
    },
  ];

  const hasIncomingAddressDetails =
    requestDetails?.recordType === 'Address' &&
    incomingAddressFields.some(
      (field) => field.newValue !== null && field.newValue !== undefined && field.newValue !== ''
    );



  const renderEditedFieldCard = (field, index) => {
    const fieldLabel = field.fieldName || '-';
    const incomingValue = formatDcrValue(field.newValue);

    return (
      <Paper
        key={field.id || `${field.fieldName}-${index}`}
        elevation={0}
        sx={{
          border: '1px solid #dbe3ef',
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: alpha('#1E88E5', 0.06),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 700 }}>
              FIELD: <span style={{ color: '#1E88E5' }}>{fieldLabel}</span>
            </Typography>
            {/* <Chip
              size="small"
              label="PRIMARY ATTRIBUTE"
              sx={{
                height: 20,
                borderRadius: '3px',
                
                fontWeight: 800,
                color: '#fff',
                backgroundColor: '#1E88E5',
              }}
            /> */}
          </Stack>
          {/* <Typography
            variant="caption"
            sx={{ color: '#64748b', fontWeight: 600, letterSpacing: '0.04em' }}
          >
            ATTRIBUTE_ID_{field.id || index + 1}
          </Typography> */}
        </Box>

        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  minHeight: 84,
                  p: 1.25,
                  borderRadius: 1,
                  border: '1px solid #eef2f7',
                  backgroundColor: '#fff',
                }}
              >
                {/* <Typography
                  variant="caption"
                  sx={{ color: '#00856f', fontWeight: 900, display: 'block', mb: 1 }}
                >
                  {requestDetails?.recordType === 'Address'
                    ? `MASTER PRIMARY: ${String(formatDcrValue(hcpDetails.addrDcrObj?.masterPrimary || 'true')).toUpperCase()}`
                    : 'CURRENT MASTERED VALUE'}
                </Typography> */}
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Old Value
                </Typography>
                <Typography variant="caption" sx={{ color: '#1e293b', fontWeight: 600 }}>
                  {formatDcrValue(field.oldValue)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  minHeight: 84,
                  p: 1.25,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: alpha('#10b981', 0.22),
                  backgroundColor: alpha('#10b981', 0.045),
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography
                    variant="caption"
                    sx={{ color: '#00856f', fontWeight: 700, textTransform: 'uppercase' }}
                  >
                    New Value
                  </Typography>
                  {/* <Chip
                    size="small"
                    label="DELTA"
                    sx={{
                      height: 20,
                      borderRadius: '3px',
                      color: '#fff',
                      
                      fontWeight: 600,
                      backgroundColor: '#00a676',
                    }}
                  /> */}
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ color: '#1e293b', lineHeight: 1.66,  fontWeight: 600 }}
                >
                  {incomingValue}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ mt: 1.25 }}
          >
            <TextField
              fullWidth
              size="small"
              value={field.resolutionNote || ''}
              placeholder="Resolution notes (optional)"
              onChange={(event) => updateEditedField(index, 'resolutionNote', event.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: alpha('#1E88E5', 0.06),
                  borderRadius: 1,
                  height: 32,
                  
                },
                '& .MuiInputBase-root': { height: '36px' }
              }}/>
            <Button
              variant={field.mdmStatus === 'Accepted' ? 'contained' : 'outlined'}
              startIcon={<CheckIcon />}
              onClick={() => handleStatusChange(index, 'Accepted')}
              sx={{
                minWidth: 96,
                height: 34,
                textTransform: 'none',
                fontWeight: 700,
                color: field.mdmStatus === 'Accepted' ? '#fff' : '#00856f',
                borderColor: alpha('#10b981', 0.35),
                backgroundColor: field.mdmStatus === 'Accepted' ? '#10b981' : '#fff',
                '&:hover': {
                  backgroundColor:
                    field.mdmStatus === 'Accepted' ? '#059669' : alpha('#10b981', 0.08),
                  borderColor: '#10b981',
                },
              }}
            >
              Accept
            </Button>
            <Button
              variant={field.mdmStatus === 'Rejected' ? 'contained' : 'outlined'}
              startIcon={<CloseIcon />}
              onClick={() => handleStatusChange(index, 'Rejected')}
              sx={{
                minWidth: 96,
                height: 34,
                textTransform: 'none',
                fontWeight: 700,
                color: field.mdmStatus === 'Rejected' ? '#fff' : '#dc2626',
                borderColor: alpha('#ef4444', 0.3),
                backgroundColor: field.mdmStatus === 'Rejected' ? '#ef4444' : '#fff',
                '&:hover': {
                  backgroundColor:
                    field.mdmStatus === 'Rejected' ? '#dc2626' : alpha('#ef4444', 0.08),
                  borderColor: '#ef4444',
                },
              }}
            >
              Reject
            </Button>
          </Stack>
        </Box>
      </Paper>
    );
  };

  const renderEditedFieldsPaper = () => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.9,
          borderBottom: '1px solid #eef2f7',
          backgroundColor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <Box
          sx={{
            width: 14,
            height: 14,
            border: '2px solid #1E88E5',
            borderRadius: '2px',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 3,
              top: 3,
              width: 4,
              height: 4,
              bgcolor: '#1E88E5',
            },
          }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
          {requestDetails?.recordType === 'Address'
            ? 'Requested Address Details'
            : 'Request Account Details'}
        </Typography>
      </Box>
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {hasIncomingAddressDetails ? (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: alpha('#10b981', 0.24),
              borderRadius: 1,
              backgroundColor: alpha('#10b981', 0.045),
              p: 1.25,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography
                variant="caption"
                sx={{ color: '#00856f', fontWeight: 800, textTransform: 'uppercase' }}
              >
                Incoming Address Details
              </Typography>
              {agentResponse?.recommendation ? (
                <Chip
                  size="small"
                  label={formatTimelineKey(agentResponse.recommendation)}
                  sx={{
                    height: 22,
                    borderRadius: '4px',
                    color: '#fff',
                    bgcolor: '#00a676',
                    fontWeight: 700,
                    
                  }}
                />
              ) : null}
            </Stack>
            <Grid container spacing={1.25}>
              {incomingAddressFields.map((field) => (
                <Grid key={field.label} item xs={12} sm={6} md={4}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: '#64748b',
                      fontWeight: 600,
                      textTransform: 'none',
                      letterSpacing: '0.01em',
                      
                      mb: 0.35,
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700 }}>
                    {formatDcrValue(field.newValue)}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            {agentResponse?.reason ? (
              <Box
                sx={{
                  mt: 1.25,
                  p: 1.1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: alpha('#1E88E5', 0.18),
                  bgcolor: alpha('#1E88E5', 0.045),
                }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.65 }}>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: '#1E88E5',
                      boxShadow: `0 0 0 4px ${alpha('#1E88E5', 0.12)}`,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#0d47a1',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    Agent Recommendation Reason
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.55 }}>
                  {agentResponse.reason}
                </Typography>
              </Box>
            ) : null}
          </Paper>
        ) : null}
        {(() => {
          const hasMasterPrimary = editedFields.some(field => field.fieldName === 'masterPrimary');
          const filteredFields = hasMasterPrimary
            ? editedFields.filter((field) => field.fieldName === 'masterPrimary')
            : editedFields;
          return filteredFields.length ? (
            filteredFields.map((field) => {
              const originalIndex = editedFields.indexOf(field);
              return renderEditedFieldCard(field, originalIndex);
            })
          ) : (
            <Typography variant="body2" sx={{ color: '#64748b', p: 1 }}>
              No edited fields available for this request.
            </Typography>
          );
        })()}
      </Box>
    </Paper>
  );

  const renderHcpProfileSummaryPaper = () => {
    return null;
  };


const customCardStyle = {
  backgroundColor: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  p: 1.5,
  boxShadow: 'none',
  height: '100%',
};

const renderNewHeader = () => {
  const formattedNpi = hcpDetails.npiId || '1275813370';
  const formattedProf = hcpDetails.primaryProfession || 'Physician Assistants';
  const formattedSpec = hcpDetails.primarySpecialty || 'Assistant';
  const formattedSubSpec =
    hcpDetails.subSpecialty ||
    hcpTableData.subSpecialtyDetails?.[0]?.spclDesc ||
    hcpTableData.subSpecialtyDetails?.[0]?.specialtyDesc ||
    '-';
  
  const rawSla = slaCountdown || agentResponse?.sla || agentResponse?.slaRemaining || rawResponseDetails?.request_record?.sla || '-';
  const confidenceVal =
    requestDetails.recordType === 'Address'
      ? 75
      : agentResponse?.confidenceScore || agentResponse?.confidence_score || agentResponse?.confidence || 91;
  const confidenceMeta = getConfidenceMeta(confidenceVal);

  const formatDateToStandard = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const pad = (num) => String(num).padStart(2, '0');
      const yyyy = d.getFullYear();
      const mm = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const min = pad(d.getMinutes());
      const ss = pad(d.getSeconds());
      return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    } catch {
      return dateStr;
    }
  };

  const splitDateTime = (dateStr) => {
    const formatted = formatDateToStandard(dateStr);
    if (!formatted) return { date: '', time: '' };
    const parts = formatted.split(' ');
    return {
      date: parts[0] || '',
      time: parts[1] || '',
    };
  };

  const rawCreatedDate = rawResponseDetails?.request_record?.createdDate || rawResponseDetails?.current_record?.createdDate || '2026-06-23T11:37:22Z';
  const formattedCreatedDate = formatDateToStandard(rawCreatedDate);
  const { date: reqDateOnly, time: reqTimeOnly } = splitDateTime(rawCreatedDate);

  const dividerElement = (
    <Divider
      orientation="vertical"
      flexItem
      sx={{
        borderColor: '#e2e8f0',
        alignSelf: 'stretch',
        my: 0.5,
        display: { xs: 'none', lg: 'block' },
      }}
    />
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        borderLeft: '4.5px solid #0284c7',
        backgroundColor: '#fff',
        py: 1.5,
        px: 2.5,
        mb: 1.5,
        position: 'relative',
        boxShadow: 'none',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          gap: { xs: 2, lg: 1 },
          width: '100%',
        }}
      >
        {/* Block 1: DCR # & Target HCP Name */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: { xs: '100%', lg: '24%' } }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.25rem', fontFamily: '"Outfit", "Inter", sans-serif', lineHeight: 1.15 }}>
              DCR #{reqId || '20647'}
            </Typography>
            <Chip
              size="small"
              label={`${requestDetails.recordType || 'Address'} Change`}
              sx={{
                height: 20,
                borderRadius: '4px',
                fontWeight: 700,
                fontSize: '0.68rem',
                color: '#2563eb',
                bgcolor: '#eff6ff',
                border: '1px solid #dbeafe',
              }}
            />
          </Stack>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.2, display: 'block' }}>
              Managing data change request for:
            </Typography>
            <Typography variant="body2" sx={{ color: '#1E88E5', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.25, display: 'block', mt: 0.25 }}>
              {[hcpDetails.firstName, hcpDetails.middleName, hcpDetails.lastName]
                .filter(Boolean)
                .join(' ') || requestDetails.accountName || 'Priyanka Anthony Shah'}{' '}
              (MDM ID: {mdmId || hcpDetails.mdmId || '2000984766'})
            </Typography>
          </Box>
        </Box>

        {dividerElement}

        {/* Block 2: NPI */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            NPI
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontSize: '0.8rem' }}>
            {formattedNpi}
          </Typography>
        </Box>

        {dividerElement}

        {/* Block 3: Profession */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Profession
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontSize: '0.8rem', whiteSpace: 'pre-wrap', lineHeight: 1.2 }}>
            {formattedProf}
          </Typography>
        </Box>

        {dividerElement}

        {/* Block 4: Speciality */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Speciality
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontSize: '0.8rem', textTransform: 'uppercase', lineHeight: 1.2 }}>
            {formattedSpec}
          </Typography>
        </Box>

        {dividerElement}

        {/* Block 5: Sub Speciality */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Sub Speciality
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontSize: '0.8rem', textTransform: 'uppercase', lineHeight: 1.2 }}>
            {formattedSubSpec}
          </Typography>
        </Box>

        {dividerElement}

        {/* Block 6: Status */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Status
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              label={formatTimelineKey(overallStatus)}
              sx={{
                height: 20,
                borderRadius: '4px',
                fontWeight: 700,
                fontSize: '0.68rem',
                color: '#ea580c',
                bgcolor: '#fff7ed',
                border: '1px solid #ffedd5',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.58rem', mt: 0.5, fontWeight: 500 }}>
            Since {formattedCreatedDate}
          </Typography>
        </Box>

        {dividerElement}

        {/* Block 7: Confidence Score */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' }, alignItems: 'flex-start' }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
            Confidence Score
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={confidenceMeta.value}
                size={22}
                thickness={4}
                sx={{ color: confidenceMeta.color }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckIcon sx={{ color: confidenceMeta.color, fontSize: 12, strokeWidth: 3 }} />
              </Box>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>
              {confidenceMeta.value}%
            </Typography>
          </Stack>
        </Box>

        {dividerElement}

        {/* Block 8: SLA */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            SLA
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 800, color: '#ea580c', mt: 0.5, fontSize: '0.82rem' }}>
            {rawSla}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.58rem', fontWeight: 500, mt: 0.25 }}>
            Remaining
          </Typography>
        </Box>

        {dividerElement}

        {/* Block 9: Request Date */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Request Date
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontSize: '0.8rem', lineHeight: 1.2 }}>
            {reqDateOnly}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem', lineHeight: 1.2 }}>
            {reqTimeOnly}
          </Typography>
        </Box>

      </Box>
    </Paper>
  );
};

const renderNewComparisonCard = () => {
  return (
    <Paper
      sx={{
        ...customCardStyle,
        mb: 0,
        height: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E88E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            CHANGE COMPARISON
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500 }}>
            Review the current and requested changes
          </Typography>
        </Box>
      </Box>

      {(!requestDetails?.recordType || requestDetails?.recordType?.toLowerCase() === 'account') && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
            Account Information
          </Typography>
          <Grid container spacing={1.5} alignItems="stretch">
            {/* Current Account Card */}
            <Grid item xs={12} md={5.5}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0', borderRadius: '8px', bgcolor: '#fff', height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <MonitorHeartRoundedIcon sx={{ color: '#3b82f6', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#3b82f6', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    CURRENT ACCOUNT INFO
                  </Typography>
                </Stack>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {accountFields.map((field, idx) => {
                    const oldValueStr = formatDcrValue(field.oldValue);
                    const isLast = idx === accountFields.length - 1;
                    return (
                      <Box
                        key={field.label}
                        sx={{
                          display: 'flex',
                          minHeight: 40,
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                          borderBottom: isLast ? 'none' : '1px solid #e2e8f0',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                          {field.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.78rem' }}>
                          {oldValueStr}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>

            {/* Arrow Column */}
            <Grid item xs={12} md={1} sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ height: 52 }} />
              {accountFields.map((_, idx) => (
                <Box key={idx} sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                  <ArrowForwardRoundedIcon sx={{ color: '#cbd5e1', fontSize: '1.2rem' }} />
                </Box>
              ))}
            </Grid>

            {/* Requested Account Card */}
            <Grid item xs={12} md={5.5}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0', borderRadius: '8px', bgcolor: '#fff', height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <CheckCircleRoundedIcon sx={{ color: '#22c55e', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#22c55e', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    REQUESTED ACCOUNT INFO
                  </Typography>
                </Stack>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {accountFields.map((field, idx) => {
                    const oldValueStr = formatDcrValue(field.oldValue);
                    const newValueStr = formatDcrValue(field.newValue);
                    const isChanged = oldValueStr !== newValueStr;
                    const isLast = idx === accountFields.length - 1;
                    return (
                      <Box
                        key={field.label}
                        sx={{
                          display: 'flex',
                          minHeight: 40,
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                          borderBottom: isLast ? 'none' : '1px solid #e2e8f0',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                          {field.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: isChanged ? '#15803d' : '#1e293b', fontSize: '0.78rem' }}>
                          {newValueStr}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: '#f1f5f9' }} />
        </Box>
      )}

      {(!requestDetails?.recordType || requestDetails?.recordType?.toLowerCase() !== 'account') && (
        <>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
            Address Details
          </Typography>

          <Grid container spacing={1.5} alignItems="stretch">
            {/* Current Address Card */}
            <Grid item xs={12} md={5.5}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0', borderRadius: '8px', bgcolor: '#fff', height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <MonitorHeartRoundedIcon sx={{ color: '#3b82f6', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#3b82f6', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    CURRENT ADDRESS
                  </Typography>
                </Stack>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {incomingAddressFields.map((field, idx) => {
                    const oldValueStr = formatDcrValue(field.oldValue);
                    const newValueStr = formatDcrValue(field.newValue);
                    const isChanged = oldValueStr !== newValueStr;
                    const isLast = idx === incomingAddressFields.length - 1;
                    return (
                      <Box
                        key={field.label}
                        sx={{
                          display: 'flex',
                          minHeight: 40,
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                          borderBottom: isLast ? 'none' : '1px solid #e2e8f0',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                          {field.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.78rem' }}>
                          {oldValueStr}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>

            {/* Arrow Column */}
            <Grid item xs={12} md={1} sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ height: 42 }} />
              {incomingAddressFields.map((_, idx) => (
                <Box key={idx} sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                  <ArrowForwardRoundedIcon sx={{ color: '#cbd5e1', fontSize: '1.2rem' }} />
                </Box>
              ))}
            </Grid>

            {/* Requested Address Card */}
            <Grid item xs={12} md={5.5}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0', borderRadius: '8px', bgcolor: '#fff', height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleRoundedIcon sx={{ color: '#22c55e', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#22c55e', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    REQUESTED ADDRESS
                  </Typography>
                </Stack>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {incomingAddressFields.map((field, idx) => {
                    const oldValueStr = formatDcrValue(field.oldValue);
                    const newValueStr = formatDcrValue(field.newValue);
                    const isChanged = oldValueStr !== newValueStr;
                    const isLast = idx === incomingAddressFields.length - 1;
                    const useChip = isChanged && ['State', 'Zip'].includes(field.label);

                    return (
                      <Box
                        key={field.label}
                        sx={{
                          display: 'flex',
                          minHeight: 40,
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                          borderBottom: isLast ? 'none' : '1px solid #e2e8f0',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                          {field.label}
                        </Typography>
                        {useChip ? (
                          <Chip
                            size="small"
                            label={newValueStr}
                            sx={{
                              height: 20,
                              borderRadius: '4px',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              color: '#15803d',
                              bgcolor: '#dcfce7',
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 700, color: isChanged ? '#15803d' : '#1e293b', fontSize: '0.78rem' }}>
                            {newValueStr}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

    </Paper>
  );
};

const renderNewAIRecommendationCard = () => {
  if (!cardsDetails || !cardsDetails.aiRecommendation) return null;

  const rec = cardsDetails.aiRecommendation;
  const isReject = rec.recommendation.toLowerCase().includes('reject');
  
  let recommendationLabel = rec.recommendation === 'human_review_required' ? 'Human review required' : rec.recommendation;
  let recommendationBullets = rec.reasoning || [];
  let confidenceMeta = getConfidenceMeta(rec.confidence || 75);
  let recommendationColor = isReject ? '#dc2626' : (rec.status === 'human_review_required' ? '#f97316' : confidenceMeta.color);
  let recommendationBg = isReject ? '#fee2e2' : (rec.status === 'human_review_required' ? '#ffedd5' : confidenceMeta.bg);
  if (rec.recommendation === 'human_review_required') {
      recommendationLabel = 'Human review required';
      recommendationColor = '#16a34a';
      recommendationBg = '#dcfce7';
  }

  return (
    <Paper sx={{ ...customCardStyle, mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <MonitorHeartRoundedIcon sx={{ color: '#1E88E5', fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: '#1E88E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            AI RECOMMENDATION
          </Typography>
        </Stack>
      </Box>

      {workflowSteps?.some(
        (step) => step.status === 'human_review_required'
      ) ? (
        <>
          {/* Warning Banner */}
          <Box
            sx={{
              p: 1.25,
              mb: 2,
              bgcolor: recommendationBg,
              borderLeft: `4px solid ${recommendationColor}`,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
            }}
          >
            <Avatar sx={{ width: 20, height: 20, bgcolor: alpha(recommendationColor, 0.16), color: recommendationColor, fontSize: '0.75rem' }}>
              <PersonRoundedIcon sx={{ fontSize: 14 }} />
            </Avatar>
            <Typography variant="body2" sx={{ fontWeight: 700, color: recommendationColor, fontSize: '0.78rem' }}>
              {formatTimelineKey(recommendationLabel)}
            </Typography>
          </Box>

          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', textTransform: 'uppercase', mb: 1, letterSpacing: '0.02em', fontSize: '0.7rem' }}>
            Reasoning
          </Typography>

          <Box sx={{ pl: 2 , maxHeight: 120, overflowY: 'auto', pr: 1 }}>
            <ul style={{ margin: 0, paddingLeft: 0, fontSize: '0.75rem', color: '#475569', lineHeight: 1.75 }}>
              {recommendationBullets.map((item) => (
                <li key={item} style={{ marginBottom: '6px' }}>{item}</li>
              ))}
            </ul>
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2, opacity: 0.6 }}>
          <Box sx={{ width: 56, height: 56, mb: 1.5 }}>
            <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M42 20H22C19.7909 20 18 21.7909 18 24V44C18 46.2091 19.7909 48 22 48H42C44.2091 48 46 46.2091 46 44V24C46 21.7909 44.2091 20 42 20Z" fill="#F1F5F9" stroke="#94A3B8" strokeWidth="2" strokeLinejoin="round"/>
              <path d="M26 28H38" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round"/>
              <path d="M26 34H32" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="42" cy="42" r="10" fill="#ffffff" stroke="#94A3B8" strokeWidth="2"/>
              <path d="M49 49L56 56" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round"/>
              <path d="M42 38V46M38 42H46" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </Box>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textAlign: 'center', lineHeight: 1.4,fontSize:"12px" }}>
            No AI recommendation<br />available at this stage
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const renderNewEvidenceSummaryCard = () => {
  if (!cardsDetails || !cardsDetails.evidenceSummary) return null;

  const evidenceItems = cardsDetails.evidenceSummary;

  return (
    <Paper sx={{ ...customCardStyle, mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: '#1E88E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          EVIDENCE SUMMARY
        </Typography>
      </Box>

      <Stack spacing={1.5}>
        {evidenceItems?.length > 0 ? evidenceItems.map((item) => (
          <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f8fafc',gap:0.9 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.78rem', lineHeight: 1.2 }}>
                {item.label}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.68rem' }}>
                {item.desc || item.description}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.5} alignItems="center">
              {(() => {
                const isProgress = item.status && (item.status.toLowerCase().includes('in progress') || item.status.toLowerCase() === 'running');
                if (isProgress) {
                  return (
                    <>
                      <CircularProgress size={12} thickness={5} sx={{ color: '#3b82f6' }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#3b82f6', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                        {item.status}
                      </Typography>
                    </>
                  );
                }
                return (
                  <>
                    {item.passed ? (
                      <CheckIcon sx={{ color: '#22c55e', fontSize: 13, strokeWidth: 3 }} />
                    ) : (
                      <CloseIcon sx={{ color: '#ef4444', fontSize: 13, strokeWidth: 3 }} />
                    )}
                    <Typography variant="caption" sx={{ fontWeight: 700, color: item.passed ? '#22c55e' : '#ef4444', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                      {item.status}
                    </Typography>
                  </>
                );
              })()}
            </Stack>
          </Box>
        )) :(
          <Box
            sx={{
              py: 1,
              px: 1.25,
              borderRadius: '6px',
              border: '1px dashed #cbd5e1',
              bgcolor: '#f8fafc',
            }}
          >
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem' }}>
              No evidence data available.
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

const renderNewRequesterInfoCard = () => {
  if (!cardsDetails || !cardsDetails.requesterInformation) return null;

  return (
    <Paper sx={{ ...customCardStyle, mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: '#1E88E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          REQUESTER INFORMATION
        </Typography>
        <Avatar sx={{ width: 24, height: 24, bgcolor: '#eff6ff', color: '#1E88E5', fontSize: '0.75rem' }}>
          <PersonRoundedIcon sx={{ fontSize: 16 }} />
        </Avatar>
      </Box>

      <Grid container spacing={2} sx={{maxHeight: activeTabConfig?.rows?.length >= 1 ? "auto" : 120, overflow:"auto"}}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            Requested By
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.8rem' }}>
            {cardsDetails.requesterInformation.requestedBy}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            Profile
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.8rem' }}>
            {cardsDetails.requesterInformation.profile}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            Email
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', fontSize: '0.8rem', overflowWrap: 'anywhere' }}>
            {cardsDetails.requesterInformation.email}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            Request Date
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.8rem' }}>
            {cardsDetails.requesterInformation.requestDate}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            Last Modified
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.8rem' }}>
            {cardsDetails?.requesterInformation?.lastModified || '-'}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            Comments
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569', fontSize: '0.78rem', lineHeight: 1.5 }}>
            Initially requested for potential address update based on provider communication.
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

const renderRegionalManagerCard = () => {
  if (!cardsDetails || !cardsDetails.regionalManager) return null;

  return (
    <Paper sx={{ ...customCardStyle, mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: '#1E88E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          REGIONAL MANAGER
        </Typography>
        <Avatar sx={{ width: 24, height: 24, bgcolor: '#eff6ff', color: '#1E88E5', fontSize: '0.75rem' }}>
          <PersonRoundedIcon sx={{ fontSize: 16 }} />
        </Avatar>
      </Box>

      {cardsDetails?.regionalManager?.name ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: '#f1f5f9', color: '#64748b' }}>
            <PersonRoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
            {cardsDetails.regionalManager.name}
          </Typography>
          <Chip label={cardsDetails.regionalManager.region} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#e0e7ff', color: '#4338ca', mt: 0.5 }} />
          {cardsDetails.regionalManager.email && (
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
              {cardsDetails.regionalManager.email}
            </Typography>
          )}
        </Box>
      </Box> ) :(
          <Box
            sx={{
              py: 1,
              px: 1.25,
              borderRadius: '6px',
              border: '1px dashed #cbd5e1',
              bgcolor: '#f8fafc',
            }}
          >
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem' }}>
              No data available.
            </Typography>
          </Box>
        )}
    </Paper>
  );
};

  const renderNewFooter = () => {
    const oldRep = firstPresentValue(agentResponse?.oldrep, agentResponse?.oldrepName);
    const newRep = firstPresentValue(agentResponse?.newrep, agentResponse?.newrepName);
    
    // Check if both reps exist and whether they are different or the same
    const bothRepsExist = !!oldRep && !!newRep;
    const isRepsDifferent = bothRepsExist && oldRep !== newRep;
    const isRepsSame = bothRepsExist && oldRep === newRep;
    
    // Check if status is Escalated
    const currentStatus = String(requestDetails?.status || requestDetails?.mdm_dcr_status || '').toUpperCase();
    const isEscalated = currentStatus === 'ESCALATED';

    const disableApprove = isRepsDifferent && !isEscalated;
    const disableEscalate = isRepsSame;

    return (
      <Paper
        elevation={4}
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: '#fff',
          borderTop: '1px solid #e2e8f0',
          py: 1.5,
          px: 3,
          mx: -2,
          mb: -3,
          mt: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* Left Side: Add Internal Note and Feedback */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
            sx={{
              color: '#64748b',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
              '&:hover': { bgcolor: '#f1f5f9', color: '#1e293b' },
            }}
          >
            Add Internal Note
          </Button>
          <Button
            variant="outlined"
            startIcon={<ForumOutlined sx={{ fontSize: 18 }} />}
            onClick={() => setFeedbackState(prev => ({ ...prev, open: true }))}
            sx={{
              borderColor: '#1e88e5',
              color: '#1e88e5',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              borderRadius: '6px',
              padding: '6px 16px',
              '&:hover': { 
                borderColor: '#0085f9', 
                bgcolor: '#e0e7ff' 
              },
            }}
          >
            Feedback
          </Button>
        </Box>

        {/* Right Side: Reject, Escalate, Approve */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => initiateClose('REJECTED')}
            disabled={saving || closing}
            startIcon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderColor: '#fca5a5',
              color: '#dc2626',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.82rem',
              borderRadius: '6px',
              px: 2.5,
              py: 0.75,
              '&:hover': { borderColor: '#ef4444', bgcolor: '#fef2f2' },
            }}
          >
            Reject
          </Button>

          {(!requestDetails?.recordType || requestDetails?.recordType?.toLowerCase() !== 'account') && !isEscalated && (
            <Button
              variant="outlined"
              onClick={() => setEscalationModalOpen(true)}
              disabled={saving || closing || disableEscalate}
              startIcon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderColor: '#f59e0b',
                color: '#d97706',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.82rem',
                borderRadius: '6px',
                px: 2.5,
                py: 0.75,
                '&:hover': { borderColor: '#d97706', bgcolor: '#fffbeb' },
                '&.Mui-disabled': { borderColor: '#fde68a', color: '#fcd34d' },
              }}
            >
              Escalate
            </Button>
          )}

          <Button
            variant="contained"
            onClick={() => initiateClose('APPROVED')}
            disabled={saving || closing || disableApprove}
            startIcon={(saving || closing) ? <CircularProgress size={16} color="inherit" /> : <CheckIcon sx={{ fontSize: 16 }} />}
            sx={{
              bgcolor: '#10b981',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.82rem',
              borderRadius: '6px',
              px: 2.5,
              py: 0.75,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#059669', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#a7f3d0', color: '#fff' },
            }}
          >
            {saving || closing ? 'Approving...' : 'Approve'}
          </Button>
        </Stack>
      </Paper>
    );
  };




;


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 100px)' }}>
      <Box sx={{ flex: 1,mb:2 }}>
      {!requestLoadFailed &&  <Box sx={{ mb: 1 }}>
          <PageBreadcrumbs
          items={[
            { label: 'DCR Inbox', path: '/inbox/user' },
            { label: 'HCP Details', path: mdmId ? `/details/hcp/${mdmId}` : null },
            { label: reqId ? 'DCR Request' : 'New HCP Request' },
          ].filter((item) => item.label !== 'HCP Details' || item.path)}
        />
      </Box>}


      {requestLoadFailed && !loading && !loadingHcp && !loadingStatus && !loadingCards && !isPageLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: { xs: 1, sm: 2, md: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              minHeight: '72vh',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1.5,
              px: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155' }}>
              Request details not found. Please select a request from the inbox page.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/inbox/user')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5,
                borderRadius: '8px',
                bgcolor: '#1E88E5',
                '&:hover': { bgcolor: '#1565C0' },
              }}
            >
              Back to Inbox
            </Button>
          </Paper>
        </Box>
      ) : loading || loadingHcp || loadingStatus || loadingCards || isPageLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: { xs: 1, sm: 2, md: 3 } }}>
          <Skeleton variant="text" width={200} height={24} sx={{ mb: 2 }} />
          <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Skeleton variant="text" width={300} height={40} />
                <Skeleton variant="text" width={200} height={20} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="rounded" width={100} height={40} />
                <Skeleton variant="rounded" width={100} height={40} />
              </Box>
            </Box>
            <Grid container spacing={2}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={2.4} key={i}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="90%" height={24} />
                </Grid>
              ))}
            </Grid>
          </Paper>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: '12px', mb: 2 }} />
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: '12px' }} />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px', mb: 2 }} />
              <Skeleton variant="rectangular" height={250} sx={{ borderRadius: '12px', mb: 2 }} />
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {reqId ? renderNewHeader() : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 1,
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                backgroundColor: '#fff',
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.5, md: 2 },
                  py: { xs: 1.25, md: 1.35 },
                  backgroundColor: '#fff',
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    backgroundColor: '#1E88E5',
                  },
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: '#1e293b',
                      mb: 0.15,
                      fontSize: { xs: '0.95rem', md: '1.1rem' },
                    }}
                  >
                    New HCP Request
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                    Managing data change request for HCP ID:{' '}
                    <span style={{ color: '#1E88E5', fontWeight: 600 }}>
                      {mdmId || hcpDetails.mdmId || '-'}
                    </span>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  }}
                >
                  <Button className="action-btn process-btn action-btn process-btn"
                    variant="contained"
                    disableElevation
                    onClick={saveDcr}
                    disabled={saving}
                    sx={{
                      height: 32,
                      borderRadius: '4px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      backgroundColor: '#1E88E5',
                      '&:hover': {
                        backgroundColor: '#1565C0',
                      },
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button className="action-btn close-btn action-btn close-btn"
                    variant="outlined"
                    onClick={initiateClose}
                    disabled={saving || closing}
                    sx={{
                      height: 32,
                      borderRadius: '4px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      borderColor: '#e2e8f0',
                      color: '#64748b',
                      backgroundColor: '#fff',
                      '&:hover': { backgroundColor: '#f8fafc', borderColor: '#cbd5e1' },
                    }}
                  >
                    Close Request
                  </Button>
                  <Button className="action-btn cancel-btn action-btn cancel-btn"
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    disabled={saving}
                    sx={{
                      minWidth: 80,
                      height: 32,
                      color: '#2196f3',
                      borderColor: '#bfdbfe',
                      backgroundColor: '#fff',
                      '&:hover': { backgroundColor: '#eff6ff', borderColor: '#93c5fd' },
                      textTransform: 'none',
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}
          <Box sx={{ p: 0 }}>
            {reqId ? (
              <Grid container spacing={1.5} alignItems="stretch">
                <Grid item xs={12} lg={8} sx={{ display: 'flex' }}>
                  <Stack spacing={1.5} sx={{ width: '100%', height: '100%' }}>
                    {renderNewComparisonCard()}

        
                      {renderTabTable(
                        activeTabConfig.columns,
                        activeTabConfig.rows,
                        activeTabConfig.emptyMessage,
                        activeTabConfig.keyPrefix,
                        activeTabConfig.tableKey
                      )}
           
                  </Stack>
                </Grid>

                <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
                  <Stack spacing={1.5} sx={{ width: '100%', height: '100%' }}>
                    {renderNewAIRecommendationCard()}
                    {renderNewEvidenceSummaryCard()}
                    {renderNewRequesterInfoCard()}
                    {renderRegionalManagerCard()}
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      bgcolor: '#fff',
                      overflow: 'hidden',
                      '& > div': {
                        borderTop: 'none',
                        borderLeft: 'none',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <WorkflowTimelineBottomBar
                      workflowSteps={workflowSteps}
                      agentOverallStatus={normalizeOverallStatus(overallStatus)}
                    />
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} lg={12}>
                <Stack spacing={1.5}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      overflow: 'hidden',
                      backgroundColor: '#fff',
                      '& .MuiInputBase-root': {
                        
                      },
                      '& .MuiInputLabel-root': {
                        
                        color: '#64748b',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1.5,
                        borderBottom: '1px solid #eef2f7',
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          Request Details
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 0.35, color: '#64748b' }}>
                          Review request metadata before taking action.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ px: 1.5, py: 1.5 }}>
                      <Grid container spacing={1.25}>
                        <Grid item xs={12} sm={6} md={2.4}>
                          <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>Request Type</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            select
                            value={requestDetails.requestType}
                            onChange={handleRequestTypeChange}
                            disabled={areRequestFieldsReadOnly}
                            sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                            {requestTypeOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                          <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>Record Type</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            select
                            
                            value={requestDetails.recordType}
                            onChange={handleRecordTypeChange}
                            disabled={areRequestFieldsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                            {recordTypeOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                          <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>Request Id</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder='Enter Request Id'
                            value={requestDetails.requestId || reqId || ''}
                            disabled={areRequestFieldsReadOnly}
                            onChange={(event) =>
                              setRequestDetails((prev) => ({
                                ...prev,
                                requestId: event.target.value,
                              }))
                            }
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                          <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>MDM Id</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            
                            value={requestDetails.accountId || hcpDetails.mdmId}
                            disabled
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                          <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>Account Name</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            
                            value={
                              requestDetails.accountName ||
                              [hcpDetails.firstName, hcpDetails.lastName].filter(Boolean).join(' ')
                            }
                            disabled
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 1.25 }} />

                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1, color: '#0f172a',  }}
                      >
                        Requestor Details
                      </Typography>
                      <Grid container spacing={1.25}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>Name</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            
                            value={requestorDetails.name}
                            disabled
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>Email</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            
                            placeholder={getInputPlaceholder('Email')}
                            value={requestorDetails.email}
                            disabled={areRequestFieldsReadOnly}
                            onChange={(event) =>
                              setRequestorDetails((prev) => ({
                                ...prev,
                                email: event.target.value,
                              }))
                            }
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                        </Grid>
                      </Grid>

                      {/* {requestDetails?.recordType === 'Address' ? (
                        <>
                          <Divider sx={{ my: 1.25 }} />
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, mb: 1, color: '#0f172a',  }}
                          >
                            Requested Address Details
                          </Typography>
                          <Grid container spacing={1.25}>
                            <Grid item xs={12} sm={6} lg={3}>
                              <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>Address Line 1</Typography>
                              <TextField
                                fullWidth
                                size="small"
                                
                                value={hcpDetails.addrDcrObj?.addrLn1 || ''}
                                disabled
                               sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                              <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>Address Line 2</Typography>
                              <TextField
                                fullWidth
                                size="small"
                                
                                value={hcpDetails.addrDcrObj?.addrLn2 || ''}
                                disabled
                               sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2}>
                              <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>City</Typography>
                              <TextField
                                fullWidth
                                size="small"
                                
                                value={hcpDetails.addrDcrObj?.cityNm || ''}
                                disabled
                               sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2}>
                              <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>State</Typography>
                              <TextField
                                fullWidth
                                size="small"
                                
                                value={hcpDetails.addrDcrObj?.stCd || ''}
                                disabled
                               sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2}>
                              <Typography sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.2 }}>Zip5</Typography>
                              <TextField
                                fullWidth
                                size="small"
                                
                                value={hcpDetails.addrDcrObj?.zipCd?.[0] || ''}
                                disabled
                               sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                            </Grid>
                          </Grid>
                        </>
                      ) : null} */}
                    </Box>
                  </Paper>
                  {reqId && renderEditedFieldsPaper()}
                  {reqId && shouldShowProfileSection ? renderHcpProfileSummaryPaper() : null}
                </Stack>
              </Grid>
            </Grid>
            )}
          </Box>

          {shouldShowProfileSection && !reqId ? (
            <Grid container spacing={2}>
              <Grid item xs={12} lg={12}>
                <Paper
                  elevation={0}
                  sx={{ borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}
                >
                  <Box
                    sx={{
                      px: 2.5,
                      py: 1.75,
                      borderBottom: '1px solid #eef2f7',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      HCP Details
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2.5,pt: 1.4 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={2.4}>
                        <Typography sx={{ fontWeight: 600, color: '#475569',mb:0.2, display: 'block', mb: 0.2 }}>First Name</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          
                          placeholder={getInputPlaceholder('First Name')}
                          value={hcpDetails.firstName}
                          onChange={(event) => updateHcpField('firstName', event.target.value)}
                          disabled={isHcpDetailsReadOnly}
                         sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                      </Grid>
                      <Grid item xs={12} md={2.4}>
                        <Typography sx={{ fontWeight: 600, color: '#475569',mb:0.2, display: 'block', mb: 0.2 }}>Middle Name</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          
                          placeholder={getInputPlaceholder('Middle Name')}
                          value={hcpDetails.middleName}
                          onChange={(event) => updateHcpField('middleName', event.target.value)}
                          disabled={isHcpDetailsReadOnly}
                         sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                      </Grid>
                      <Grid item xs={12} md={2.4}>
                        <Typography sx={{ fontWeight: 600, color: '#475569',mb:0.2, display: 'block', mb: 0.2 }}>Last Name</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          
                          placeholder={getInputPlaceholder('Last Name')}
                          value={hcpDetails.lastName}
                          onChange={(event) => updateHcpField('lastName', event.target.value)}
                          disabled={isHcpDetailsReadOnly}
                         sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                      </Grid>
                      <Grid item xs={12} md={2.4}>
                        <Typography sx={{ fontWeight: 600, color: '#475569',mb:0.2, display: 'block', mb: 0.2 }}>Suffix</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          
                          placeholder={getInputPlaceholder('Suffix')}
                          value={hcpDetails.suffix}
                          onChange={(event) => updateHcpField('suffix', event.target.value)}
                          disabled={isHcpDetailsReadOnly}
                         sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                      </Grid>
                      <Grid item xs={12} md={2.4}>
                        <Typography sx={{ fontWeight: 600, color: '#475569',mb:0.2, display: 'block', mb: 0.2 }}>Birth Year</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          
                          placeholder={getInputPlaceholder('Birth Year')}
                          value={hcpDetails.birthYr}
                          onChange={(event) => updateHcpField('birthYr', event.target.value)}
                          disabled={isHcpDetailsReadOnly}
                         sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                      </Grid>                
                      <Grid item xs={12} md={2.4}>
                        <Typography sx={{ fontWeight: 600, color: '#475569',mb:0.2, display: 'block', mb: 0.2 }}>Primary Specialty</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          
                          value={hcpDetails.primarySpecialty}
                          onChange={(event) =>
                            updateHcpField('primarySpecialty', event.target.value)
                          }
                          disabled={isHcpDetailsReadOnly}
                         sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                          {specialtyOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={2.4}>
                        <Typography sx={{ fontWeight: 600, color: '#475569',mb:0.2, display: 'block', mb: 0.2 }}>Primary Profession</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          placeholder='Select Primary Profession'
                          value={hcpDetails.primaryProfession}
                          onChange={(event) =>
                            updateHcpField('primaryProfession', event.target.value)
                          }
                          disabled={isHcpDetailsReadOnly}
                         sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                          {professionOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={2.4}>
                        <Typography sx={{ fontWeight: 600, color: '#475569',mb:0.2, display: 'block', mb: 0.2 }}>Status</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          
                          value={hcpDetails.status}
                          onChange={(event) => updateHcpField('status', event.target.value)}
                          disabled={isHcpDetailsReadOnly}
                         sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                          {statusOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4.8}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            display: 'block',
                            ml: 0.5,
                            mb: 0.4,
                          }}
                        >
                          Gender
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Box
                            onClick={() =>
                              !isHcpDetailsReadOnly && updateHcpField('gender', 'Male')
                            }
                            sx={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              py: 0.8,
                              px: 1.5,
                              borderRadius: '4px',
                              cursor: isHcpDetailsReadOnly ? 'not-allowed' : 'pointer',
                              backgroundColor: hcpDetails.gender === 'Male' ? '#1E88E5' : '#fff',
                              color: hcpDetails.gender === 'Male' ? '#fff' : '#64748b',
                              border: '1px solid',
                              borderColor: hcpDetails.gender === 'Male' ? '#1E88E5' : '#e2e8f0',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              opacity: isHcpDetailsReadOnly ? 0.65 : 1,
                              fontWeight: 600,
                              '&:hover': {
                                borderColor: isHcpDetailsReadOnly
                                  ? hcpDetails.gender === 'Male'
                                    ? '#1E88E5'
                                    : '#e2e8f0'
                                  : '#1E88E5',
                                color: isHcpDetailsReadOnly
                                  ? hcpDetails.gender === 'Male'
                                    ? '#fff'
                                    : '#64748b'
                                  : hcpDetails.gender === 'Male'
                                    ? '#fff'
                                    : '#1E88E5',
                                backgroundColor: isHcpDetailsReadOnly
                                  ? hcpDetails.gender === 'Male'
                                    ? '#1E88E5'
                                    : '#fff'
                                  : hcpDetails.gender === 'Male'
                                    ? '#1E88E5'
                                    : '#f8fbfc',
                              },
                            }}
                          >
                            Male
                          </Box>

                          <Box
                            onClick={() =>
                              !isHcpDetailsReadOnly && updateHcpField('gender', 'Female')
                            }
                            sx={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              py: 0.4,
                              px: 1.5,
                              borderRadius: '4px',
                              cursor: isHcpDetailsReadOnly ? 'not-allowed' : 'pointer',
                              backgroundColor: hcpDetails.gender === 'Female' ? '#1E88E5' : '#fff',
                              color: hcpDetails.gender === 'Female' ? '#fff' : '#64748b',
                              border: '1px solid',
                              borderColor: hcpDetails.gender === 'Female' ? '#1E88E5' : '#e2e8f0',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              opacity: isHcpDetailsReadOnly ? 0.65 : 1,
                              fontWeight: 600,
                              
                              '&:hover': {
                                borderColor: isHcpDetailsReadOnly
                                  ? hcpDetails.gender === 'Female'
                                    ? '#1E88E5'
                                    : '#e2e8f0'
                                  : '#1E88E5',
                                color: isHcpDetailsReadOnly
                                  ? hcpDetails.gender === 'Female'
                                    ? '#fff'
                                    : '#64748b'
                                  : hcpDetails.gender === 'Female'
                                    ? '#fff'
                                    : '#1E88E5',
                                backgroundColor: isHcpDetailsReadOnly
                                  ? hcpDetails.gender === 'Female'
                                    ? '#1E88E5'
                                    : '#fff'
                                  : hcpDetails.gender === 'Female'
                                    ? '#1E88E5'
                                    : '#f8fbfc',
                              },
                            }}
                          >
                            Female
                          </Box>

                          <Box
                            onClick={() =>
                              !isHcpDetailsReadOnly && updateHcpField('gender', 'Unknown')
                            }
                            sx={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              py: 0.4,
                              px: 1.5,
                              borderRadius: '4px',
                              cursor: isHcpDetailsReadOnly ? 'not-allowed' : 'pointer',
                              backgroundColor: hcpDetails.gender === 'Unknown' ? '#1E88E5' : '#fff',
                              color: hcpDetails.gender === 'Unknown' ? '#fff' : '#64748b',
                              border: '1px solid',
                              borderColor: hcpDetails.gender === 'Unknown' ? '#1E88E5' : '#e2e8f0',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              opacity: isHcpDetailsReadOnly ? 0.65 : 1,
                              fontWeight: 600,
                              
                              '&:hover': {
                                borderColor: isHcpDetailsReadOnly
                                  ? hcpDetails.gender === 'Unknown'
                                    ? '#1E88E5'
                                    : '#e2e8f0'
                                  : '#1E88E5',
                                color: isHcpDetailsReadOnly
                                  ? hcpDetails.gender === 'Unknown'
                                    ? '#fff'
                                    : '#64748b'
                                  : hcpDetails.gender === 'Unknown'
                                    ? '#fff'
                                    : '#1E88E5',
                                backgroundColor: isHcpDetailsReadOnly
                                  ? hcpDetails.gender === 'Unknown'
                                    ? '#1E88E5'
                                    : '#fff'
                                  : hcpDetails.gender === 'Unknown'
                                    ? '#1E88E5'
                                    : '#f8fbfc',
                              },
                            }}
                          >
                            Other
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>OK Individual ID</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.okIndvId || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>NPI</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.npiId || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>IMS</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.imsId || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>AMA</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.amaId || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>SLN</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.slnId || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>Full Profile</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.isGoldenUniverse === true
                            ? 'Yes'
                            : hcpDetails.isGoldenUniverse === false
                              ? 'No'
                              : hcpDetails.isGoldenUniverse || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>Address Line 1</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.primaryAddress.addrLn1 || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>Address Line 2</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.primaryAddress.addrLn2 || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>City</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.primaryAddress.cityNm || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>State</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.primaryAddress.stCd || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>Zip5</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.primaryAddress.zipCd?.[0] || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography sx={{  fontWeight: 600, color: '#364152', mb: 0.2 }}>Zip4</Typography>
                        <Typography sx={{  fontWeight: 400, color: '#0f172a' }}>
                          {hcpDetails.primaryAddress.zipCd?.[1] || '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>

              {requestDetails?.recordType === 'Address' ? (
                <Grid item xs={12} lg={12}>
                  <Paper
                    elevation={0}
                    sx={{ borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}
                  >
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1.75,
                        borderBottom: '1px solid #eef2f7',
                        backgroundColor: '#f8fafc',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Address Details
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Typography sx={{ fontWeight: 600, color: '#364152', fontSize: '12px', display: 'block', mb: 0.2 }}>Address Line 1</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            
                            value={hcpDetails.addrDcrObj?.addrLn1 || ''}
                            onChange={(event) =>
                              updateAddressDcrField('addrLn1', event.target.value)
                            }
                            disabled={areAddressDetailsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography sx={{ fontWeight: 600, color: '#364152', fontSize: '12px', display: 'block', mb: 0.2 }}>Address Line 2</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            
                            value={hcpDetails.addrDcrObj?.addrLn2 || ''}
                            onChange={(event) =>
                              updateAddressDcrField('addrLn2', event.target.value)
                            }
                            disabled={areAddressDetailsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography sx={{ fontWeight: 600, color: '#364152', fontSize: '12px', display: 'block', mb: 0.2 }}>City</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            
                            value={hcpDetails.addrDcrObj?.cityNm || ''}
                            onChange={(event) =>
                              updateAddressDcrField('cityNm', event.target.value)
                            }
                            disabled={areAddressDetailsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography sx={{ fontWeight: 600, color: '#364152', fontSize: '12px', display: 'block', mb: 0.2 }}>State</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            select
                            
                            value={hcpDetails.addrDcrObj?.stCd || ''}
                            onChange={(event) => updateAddressDcrField('stCd', event.target.value)}
                            disabled={areAddressDetailsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                            {facetOptions.state.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography sx={{ fontWeight: 600, color: '#364152', fontSize: '12px', display: 'block', mb: 0.2 }}>Zip5</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            
                            value={hcpDetails.addrDcrObj?.zipCd?.[0] || ''}
                            onChange={(event) => updateAddressZipField(0, event.target.value)}
                            disabled={areAddressDetailsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Typography sx={{ fontWeight: 600, color: '#364152', fontSize: '12px', display: 'block', mb: 0.2 }}>Category</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            select
                            
                            value={hcpDetails.addrDcrObj?.category || ''}
                            onChange={(event) =>
                              updateAddressDcrField('category', event.target.value)
                            }
                            disabled={areAddressDetailsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                            {ADDRESS_CATEGORY_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography sx={{ fontWeight: 600, color: '#364152', fontSize: '12px', display: 'block', mb: 0.2 }}>Setting</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            select
                            
                            value={hcpDetails.addrDcrObj?.setting || ''}
                            onChange={(event) =>
                              updateAddressDcrField('setting', event.target.value)
                            }
                            disabled={areAddressDetailsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                            {ADDRESS_SETTING_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography sx={{ fontWeight: 600, color: '#364152', fontSize: '12px', display: 'block', mb: 0.2 }}>Is Sampleable</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            select
                            
                            value={hcpDetails.addrDcrObj?.isSampleable || ''}
                            onChange={(event) =>
                              updateAddressDcrField('isSampleable', event.target.value)
                            }
                            disabled={areAddressDetailsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                            {ADDRESS_SAMPLEABLE_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography sx={{ fontWeight: 600, color: '#364152', fontSize: '12px', display: 'block', mb: 0.2 }}>Status</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            select
                            
                            value={hcpDetails.addrDcrObj?.statusLabel || ''}
                            onChange={(event) =>
                              updateAddressDcrField('statusLabel', event.target.value)
                            }
                            disabled={areAddressDetailsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                            {ADDRESS_STATUS_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography sx={{ fontWeight: 600, color: '#364152', fontSize: '12px', display: 'block', mb: 0.2 }}>Master Primary</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            select
                            
                            value={hcpDetails.addrDcrObj?.masterPrimary || ''}
                            onChange={(event) =>
                              updateAddressDcrField('masterPrimary', event.target.value)
                            }
                            disabled={areAddressDetailsReadOnly}
                           sx={{ '& .MuiInputBase-root': { height: '36px' } }} >
                            {ADDRESS_MASTER_PRIMARY_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              ) : null}
            </Grid>
          ) : null}
        </Box>
      )}

      {/* Confirmation Modal */}

      <Dialog
        open={confirmModal.open}
        onClose={() => setConfirmModal({ ...confirmModal, open: false })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            p: 2,
            width:"25rem",
            borderRadius: '12px',
            textAlign: 'center',
            backgroundColor: '#ffffff',
            backgroundClip: 'padding-box',
            boxShadow: '0 24px 48px -12px rgba(0,0,0,0.15)',
            overflow: 'visible',
            position: 'relative',
          },
        }}
      >
        <IconButton
          onClick={() => setConfirmModal({ ...confirmModal, open: false })}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: '#0f172a',
            p: 0.5,
            '&:hover': { backgroundColor: '#f1f5f9' },
          }}
        >
          <CloseIcon sx={{  strokeWidth: 1.5,color:"#adadad" }} />
        </IconButton>

        <DialogContent sx={{ pb: 1.5, px: 1, pt: 2 }}>
          {/* Icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 1.5,
            }}
          >
            {confirmModal.isDanger ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 30,
                  height: 30,
                  mb:1,
                 backgroundColor: '#FFFBEB', // Light yellow
                 borderRadius: '50%',
                 boxShadow: '0 0 0 10px #FFFBEB', // Soft yellow outer ring
                }}
              >
                <WarningAmberRoundedIcon sx={{ fontSize: 24, color: '#D97706',}} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 44,
                  height: 44,
                  backgroundColor: '#eef2ff',
                  borderRadius: '50%',
                  boxShadow: '0 0 0 10px #f5f7ff',
                }}
              >
                <HelpOutlineRoundedIcon sx={{ fontSize: 24, color: '#3b82f6' }} />
              </Box>
            )}
          </Box>

          {/* Message */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#0f172a',
              mb: 1.5,
              
              lineHeight: 1.3,
              fontFamily: '"Inter", sans-serif',
            }}
          >
            {confirmModal.message}
          </Typography>
          
          {/* <Typography variant="body2" sx={{ color: '#64748b',  lineHeight: 1.5, mb: 1, px: 2 }}>
            Your changes will be saved or discarded depending on your action. If you need to make any adjustments, you can always come back.
          </Typography> */}
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'center',
            gap: 1.5,
            pt: 0,
            px: 1,
            pb: 1,
            flexDirection: confirmModal.isDanger ? 'row' : 'row',
          }}
        >
          {confirmModal.isSuccess ? (
            // Approve action — green confirm button
            <>
              <Button className="action-btn cancel-btn"
                onClick={() => setConfirmModal({ ...confirmModal, open: false })}
                variant="outlined"
                disableElevation
                fullWidth
                disabled={closing}
                sx={{
                  py: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#334155',
                  borderColor: '#cbd5e1',
                  backgroundColor: '#ffffff',
                  '&:hover': { backgroundColor: '#f8fafc', borderColor: '#94a3b8' },
                }}
              >
                Cancel
              </Button>
              <Button className="action-btn process-btn"
                onClick={confirmModal.onConfirm}
                variant="contained"
                disableElevation
                fullWidth
                disabled={closing}
                sx={{
                  py: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  backgroundColor: '#10b981',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#059669' },
                }}
              >
                {closing ? <CircularProgress size={24} color="inherit" /> : (confirmModal.confirmText || 'Approve')}
              </Button>

            </>
          ) : confirmModal.isDanger ? (
            // Reject / danger action — red confirm button
            <>
              <Button className="action-btn cancel-btn"
                onClick={() => setConfirmModal({ ...confirmModal, open: false })}
                variant="outlined"
                disableElevation
                fullWidth
                disabled={closing}
                sx={{
                  py: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#334155',
                  borderColor: '#cbd5e1',
                  backgroundColor: '#ffffff',
                  '&:hover': { backgroundColor: '#f8fafc', borderColor: '#94a3b8' },
                }}
              >
                Cancel
              </Button>
              <Button className="action-btn process-btn"
                onClick={confirmModal.onConfirm}
                variant="contained"
                disableElevation
                fullWidth
                disabled={closing}
                sx={{
                  py: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  backgroundColor: '#dc2626 !important',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#b91c1c' },
                }}
              >
                {closing ? <CircularProgress size={24} color="inherit" /> : (confirmModal.confirmText || 'Delete')}
              </Button>
            </>
          ) : (
            // Default action — blue confirm button
            <>
              <Button className="action-btn cancel-btn"
                onClick={() => setConfirmModal({ ...confirmModal, open: false })}
                variant="outlined"
                disableElevation
                fullWidth
                disabled={closing}
                sx={{
                  py: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#334155',
                  borderColor: '#cbd5e1',
                  backgroundColor: '#ffffff',
                  '&:hover': { backgroundColor: '#f8fafc', borderColor: '#94a3b8' },
                }}
              >
                Cancel
              </Button>
              <Button className="action-btn process-btn"
                onClick={confirmModal.onConfirm}
                variant="contained"
                disableElevation
                fullWidth
                disabled={closing}
                sx={{
                  py: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#1d4ed8' },
                }}
              >
                {closing ? <CircularProgress size={24} color="inherit" /> : (confirmModal.confirmText || 'Confirm')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
        {/* renderTabTable moved inside loading check */}
      </Box>

      <Dialog open={escalationModalOpen} onClose={() => setEscalationModalOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ m: 0, p: 1, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.5, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <WarningAmberRoundedIcon sx={{ color: '#f97316', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                Address Change Request
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setEscalationModalOpen(false)}
              sx={{ color: '#64748b' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ color: '#64748b', ml: 5 }}>
            This change affects territory ownership and requires Regional Manager approval.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: '#ffffff', overflowX: 'hidden' , mt: 1}}>
          <Stack spacing={2.5}>
            {/* Territory Conflict Detected Card */}
            <Paper sx={{ p: 2.5, bgcolor: '#fffbf0', border: '1px solid #fde047', borderRadius: '8px', boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                <WarningAmberRoundedIcon sx={{ color: '#ea580c', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ea580c', letterSpacing: '0.05em', fontSize: '0.8rem' }}>
                  TERRITORY CHANGE REQUEST
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#334155', mb: 1, fontWeight: 500 }}>
                The requested address places this physician inside {firstPresentValue(agentResponse?.newterritory, agentResponse?.newterritoryName) || 'Unknown'}.
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 700, display: 'block', mb: 0.5 }}>Current Assigned Rep</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <PersonRoundedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{firstPresentValue(agentResponse?.oldrep, agentResponse?.oldrepName) || 'Unknown Rep'}</Typography>
                  </Box>
                  <Chip label={firstPresentValue(agentResponse?.oldterritory, agentResponse?.oldterritoryName) || 'Unknown'} size="small" sx={{ height: 24, fontSize: '0.75rem', fontWeight: 700, bgcolor: '#eff6ff', color: '#2563eb', borderRadius: '4px' }} />
                </Box>
                
                <ArrowForwardRoundedIcon sx={{ color: '#334155' }} />
                
                <Box sx={{ flex: 1, pl: 4 }}>
                  <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 700, display: 'block', mb: 0.5 }}>Requested Territory Rep</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <PersonRoundedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{firstPresentValue(agentResponse?.newrep, agentResponse?.newrepName) || 'Unknown Rep'}</Typography>
                  </Box>
                  <Chip label={firstPresentValue(agentResponse?.newterritory, agentResponse?.newterritoryName) || 'Unknown'} size="small" sx={{ height: 24, fontSize: '0.75rem', fontWeight: 700, bgcolor: '#eff6ff', color: '#2563eb', borderRadius: '4px' }} />
                </Box>
              </Box>

              <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                Regional approval is required before proceeding.
              </Typography>
            </Paper>

            {/* Email Section */}
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #e2e8f0' }}>
                <EmailIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2563eb', letterSpacing: '0.05em', fontSize: '0.8rem' }}>
                  EMAIL TO REGIONAL MANAGER
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 600, display: 'block', mb: 0.5 }}>To</Typography>
                    <Autocomplete
                      freeSolo
                      options={escalationRecipients.map((r) => r.email)}
                      getOptionLabel={(option) => option || ''}
                      value={escalationToEmail || ''}
                      onChange={(_, nextValue) => {
                        const emailValue = nextValue || '';
                        setEscalationToEmail(emailValue);
                        const recipient = escalationRecipients.find((item) => item.email === emailValue);
                        setEscalationToName(recipient ? recipient.name : 'Regional Manager');
                      }}
                      onInputChange={(_, nextValue) => {
                        setEscalationToEmail(nextValue || '');
                      }}
                      renderInput={(params) => {
                        const isToEmailInvalid = escalationToEmail.length > 0 && !isValidEmail(escalationToEmail);
                        return (
                          <TextField
                            {...params}
                            size="small"
                            error={isToEmailInvalid}
                            helperText={isToEmailInvalid ? 'Please enter a valid email address' : ''}
                            placeholder="Select or enter recipient email"
                            sx={{
                              '& .MuiInputBase-root': {
                                minHeight: '40px',
                                alignItems: 'center',
                                paddingTop: '0px',
                                paddingBottom: '0px',
                              },
                              '& .MuiAutocomplete-inputRoot': {
                                paddingTop: '0px !important',
                                paddingBottom: '0px !important',
                              },
                            }}
                            InputProps={{
                              ...params.InputProps,
                              sx: {
                                fontSize: '0.85rem',
                                bgcolor: '#fff',
                                color: '#0f172a',
                                borderRadius: '4px',
                                minHeight: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d5db' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9ca3af' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#3b82f6',
                                  borderWidth: '1px',
                                },
                              },
                            }}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 600, display: 'block', mb: 0.5 }}>CC</Typography>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={REGIONAL_MANAGER_CC_OPTIONS}
                      value={ccEmails}
                      onChange={(_, nextValue) => setCcEmails(nextValue)}
                      renderTags={(value, getTagProps) => value.map((option, index) => {
                        const isValid = isValidEmail(option);
                        return (
                          <Chip
                            key={option}
                            variant="outlined"
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                            sx={{
                              maxWidth: '100%',
                              bgcolor: isValid ? '#eff6ff' : '#fef2f2',
                              color: isValid ? '#1d4ed8' : '#dc2626',
                              borderColor: isValid ? '#bfdbfe' : '#fecaca',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        );
                      })}
                      renderInput={(params) => {
                        const hasInvalidCcEmails = ccEmails.some((email) => !isValidEmail(email));
                        return (
                          <TextField
                            {...params}
                            size="small"
                            error={hasInvalidCcEmails}
                            helperText={hasInvalidCcEmails ? 'One or more CC email addresses are invalid' : ''}
                            placeholder={ccEmails.length ? '' : 'Add recipients'}
                            sx={{
                              '& .MuiInputBase-root': {
                                minHeight: '40px',
                                alignItems: 'center',
                                paddingTop: '0px',
                                paddingBottom: '0px',
                              },
                              '& .MuiAutocomplete-inputRoot': {
                                paddingTop: '0px !important',
                                paddingBottom: '0px !important',
                              },
                            }}
                            InputProps={{
                              ...params.InputProps,
                              sx: {
                                fontSize: '0.85rem',
                                bgcolor: '#fff',
                                color: '#0f172a',
                                borderRadius: '4px',
                                minHeight: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d5db' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9ca3af' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#3b82f6',
                                  borderWidth: '1px',
                                },
                              },
                            }}
                          />
                        );
                      }}
                    >
                    </Autocomplete>
                  </Grid>
                </Grid>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 600, display: 'block', mb: 0.5 }}>Subject</Typography>
                  <TextField 
                    size="small" 
                    fullWidth 
                    value={escalationSubject}
                    onChange={(event) => setEscalationSubject(event.target.value)}
                    InputProps={{ 
                      sx: { 
                        fontSize: '0.85rem', 
                        bgcolor: '#fff',
                        color: '#0f172a',
                        borderRadius: '4px',
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d5db' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9ca3af' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6', borderWidth: '1px' }
                      } 
                    }} 
                  />
                </Box>
                
                <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 600, display: 'block', mb: 0.5 }}>Email Body</Typography>
                <Paper sx={{ p: 0, borderRadius: '4px', border: '1px solid #cbd5e1', bgcolor: '#fff', overflow: 'hidden', boxShadow: 'none' }}>
                  {/* Toolbar */}
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff', flexWrap: 'wrap' }}>
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('undo')}>
                      <UndoIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('redo')}>
                      <RedoIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('bold')}>
                      <FormatBoldIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('italic')}>
                      <FormatItalicIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('underline')}>
                      <FormatUnderlinedIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('insertUnorderedList')}>
                      <FormatListBulletedIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('insertOrderedList')}>
                      <FormatListNumberedIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('justifyLeft')}>
                      <FormatAlignLeftIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('justifyCenter')}>
                      <FormatAlignCenterIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('justifyRight')}>
                      <FormatAlignRightIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
                    <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => applyEscalationFormat('createLink', window.prompt('Enter link URL') || '')}>
                      <InsertLinkIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                    {/* <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        Insert Field <ArrowDropDownIcon fontSize="small" />
                      </Typography>
                    </Box> */}
                  </Box>
                  {/* Text Area */}
                  <Box
                    sx={{
                      p: 1,
                      minHeight: 250,
                      bgcolor: '#fff',
                      maxHeight: 340,
                      overflow: 'auto',
                    }}
                  >
                    {escalationLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Box
                        ref={escalationBodyRef}
                        contentEditable={!escalationSending}
                        suppressContentEditableWarning
                        role="textbox"
                        tabIndex={0}
                        data-placeholder="Generated email body will appear here."
                        onInput={updateEscalationBodyFromEditor}
                        onBlur={updateEscalationBodyFromEditor}
                        sx={{
                          minHeight: 220,
                          px: 1,
                          py: 0.5,
                          border: 'none',
                          boxShadow: 'none',
                          color: '#1e293b',
                          fontFamily: 'inherit',
                          outline: 'none',
                          borderRadius: '4px',
                          '&:focus': {
                            outline: 'none',
                            boxShadow: 'none',
                          },
                          '&:empty:before': {
                            content: '"Generated email body will appear here."',
                            color: '#94a3b8',
                            pointerEvents: 'none',
                          },
                          '& p': { margin: '0 0 12px 0' },
                          '& ul': { margin: '0 0 12px 20px' },
                          '& li': { marginBottom: '6px' },
                        }}
                      />
                    )}
                  </Box>
                </Paper>
              </Box>
            </Box>

            {/* Additional Comments Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2563eb', letterSpacing: '0.05em', fontSize: '0.8rem', mb: 1 }}>
                ADDITIONAL COMMENTS (OPTIONAL)
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                placeholder="Add any additional context or comments for the Regional Manager..."
                value={escalationComments}
                onChange={(event) => setEscalationComments(event.target.value)}
                InputProps={{ sx: { fontSize: '0.85rem', bgcolor: '#fff' } }}
              />
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', color: '#64748b', mt: 0.5, fontWeight: 600 }}>
                {`${escalationComments.length}/500`}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, pb: 3, bgcolor: '#ffffff' }}>
          <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockOutlinedIcon sx={{ color: '#94a3b8', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                This escalation will be logged and tracked in the DCR history.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setEscalationModalOpen(false)}
                sx={{ textTransform: 'none', fontWeight: 700, px: 4, py: 1, borderColor: '#cbd5e1', color: '#334155', borderRadius: '8px', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleEscalationSend}
                startIcon={escalationLoading || escalationSending ? null : <SendIcon sx={{ fontSize: 18 }} />}
                disabled={escalationLoading || escalationSending}
                sx={{ textTransform: 'none', fontWeight: 700, px: 4, py: 1, bgcolor: '#2563eb', borderRadius: '8px', '&:hover': { bgcolor: '#1d4ed8' }, boxShadow: 'none' }}
              >
                {escalationSending || escalationLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} color="inherit" />
                    <span>{escalationActionLabel}</span>
                  </Stack>
                ) : (
                  escalationActionLabel
                )}
              </Button>
            </Stack>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={feedbackState.open} 
        onClose={() => { if (!feedbackSubmitting) setFeedbackState({ open: false, evidenceText: '', recommendationText: '', evidenceResult: '', recommendationResult: '' }); }}
        PaperProps={{
          sx: {
            width: 670,
            maxWidth: "90vw",
            borderRadius: '12px'
          },
        }}
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', p: 2, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha('#1d8ef0', 0.1), color: '#1d8ef0', width: 32, height: 32 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '1rem' }}>Provide AI Feedback</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block',fontSize:12 }}>Help us improve AI recommendations</Typography>
            </Box>
          </Stack>
          <IconButton 
            onClick={() => { if (!feedbackSubmitting) setFeedbackState({ open: false, evidenceText: '', recommendationText: '', evidenceResult: '', recommendationResult: '' }); }}
            size="small"
            disabled={feedbackSubmitting}
            sx={{ color: '#64748b', '&:hover': { color: '#0f172a', bgcolor: '#f1f5f9' }, mt: 0.5 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 3 }}>
          <Stack spacing={3} sx={{ mt: 3 }}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha('#1d8ef0', 0.1), color: '#1d8ef0', width: 36, height: 36 }}>
                  <PublicRoundedIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                    Internet Search / Evidence result <span style={{color: '#ef4444'}}>*</span>
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: 11 }}>
                    Was the internet search result relevant and useful?
                  </Typography>
                </Box>
                <Box>
                  <ToggleButtonGroup
                    value={feedbackState.evidenceResult}
                    exclusive
                    onChange={(e, val) => {
                      if (val !== null) setFeedbackState(prev => ({ ...prev, evidenceResult: val, evidenceText: val === 'good' ? '' : prev.evidenceText }));
                    }}
                    size="small"
                    sx={{
                      gap: 1,
                      '& .MuiToggleButtonGroup-grouped': {
                        border: '1px solid !important',
                        borderColor: '#e2e8f0',
                        borderRadius: '6px !important',
                        color: '#64748b',
                        px: 1.5,
                        py: 0.5,
                        '&.Mui-selected': {},
                        '&:not(:first-of-type)': {
                          marginLeft: '0 !important',
                        }
                      }
                    }}
                  >
                    <ToggleButton 
                      value="good" 
                      sx={{ 
                        textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', width: 145, whiteSpace: 'nowrap',
                        color: feedbackState.evidenceResult === 'good' ? '#1d8ef0' : '#64748b',
                        borderColor: feedbackState.evidenceResult === 'good' ? '#1d8ef0 !important' : '#e2e8f0 !important',
                        bgcolor: feedbackState.evidenceResult === 'good' ? alpha('#1d8ef0', 0.08) : 'transparent',
                        '&.Mui-selected': {
                          color: '#1d8ef0',
                          bgcolor: alpha('#1d8ef0', 0.08),
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: alpha('#1d8ef0', 0.12),
                        }
                      }}
                    >
                      {feedbackState.evidenceResult === 'good' ? <CheckCircleRoundedIcon sx={{ fontSize: 18, mr: 0.75, color: '#1d8ef0' }} /> : null}
                      <ThumbUpOutlinedIcon sx={{ fontSize: 15, mr: 0.75, mb: 0.25 }} /> Good
                    </ToggleButton>
                    <ToggleButton 
                      value="needs_work" 
                      sx={{ 
                        textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', width: 145, whiteSpace: 'nowrap',
                        color: '#f97316',
                        borderColor: feedbackState.evidenceResult === 'needs_work' ? '#f97316 !important' : alpha('#f97316', 0.4) + ' !important',
                        bgcolor: feedbackState.evidenceResult === 'needs_work' ? alpha('#f97316', 0.08) : 'transparent',
                        '&.Mui-selected': {
                          color: '#f97316',
                          bgcolor: alpha('#f97316', 0.08),
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: alpha('#f97316', 0.12),
                        }
                      }}
                    >
                      {feedbackState.evidenceResult === 'needs_work' ? <CheckCircleRoundedIcon sx={{ fontSize: 18, mr: 0.75, color: '#f97316' }} /> : null}
                      <WarningAmberRoundedIcon sx={{ fontSize: 15, mr: 0.75, mb: 0.25 }} /> Needs work
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Stack>

              {feedbackState.evidenceResult === 'good' && (
                <Box sx={{ bgcolor: '#f0fdf4', p: 1.1, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleRoundedIcon sx={{ fontSize: 22, color: '#22c55e' }} />
                  <Typography variant="body2" sx={{ fontWeight: 400, color: '#16a34a',  fontSize:"12px", }}>Thanks! Your feedback for internet search has been recorded.</Typography>
                </Box>
              )}

            {feedbackState.evidenceResult === 'needs_work' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
                  Evidence Details <span style={{color: '#ef4444'}}>*</span>
                </Typography>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="Describe the issue with the evidence result..."
                    value={feedbackState.evidenceText}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFeedbackState(prev => ({ ...prev, evidenceText: e.target.value }));
                      }
                    }}
                    disabled={feedbackSubmitting}
                    error={!feedbackState.evidenceText.trim()}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                        '&.Mui-focused fieldset': { borderColor: '#1d8ef0', borderWidth: '1px' },
                        '&.Mui-error fieldset': { borderColor: '#ef4444' },
                        pb: 3
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ position: 'absolute', bottom: 6, right: 10, color: '#94a3b8', fontSize: '0.7rem' }}>
                    {feedbackState.evidenceText.length} / 500
                  </Typography>
                </Box>
                {!feedbackState.evidenceText.trim() ? (
                  <Typography variant="caption" sx={{ color: '#ef4444', mt: 1, fontSize: "12px", display: 'block' }}>
                    Evidence details are required
                  </Typography>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1, color: '#64748b' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption" style={{fontSize:"12px"}}>
                      Provide specific details so we can improve better.
                    </Typography>
                  </Stack>
                )}
              </Box>
            )}
            </Box>

            <Divider sx={{ borderStyle: 'dashed', borderColor: '#e2e8f0' }} />

            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', width: 36, height: 36 }}>
                  <StarBorderRoundedIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                    Recommendation result <span style={{color: '#ef4444'}}>*</span>
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: 11 }}>
                    Was the AI recommendation helpful and accurate?
                  </Typography>
                </Box>
                <Box>
                  <ToggleButtonGroup
                    value={feedbackState.recommendationResult}
                    exclusive
                    onChange={(e, val) => {
                      if (val !== null) setFeedbackState(prev => ({ ...prev, recommendationResult: val, recommendationText: val === 'good' ? '' : prev.recommendationText }));
                    }}
                    size="small"
                    sx={{
                      gap: 1,
                      '& .MuiToggleButtonGroup-grouped': {
                        border: '1px solid !important',
                        borderColor: '#e2e8f0',
                        borderRadius: '6px !important',
                        color: '#64748b',
                        px: 1.5,
                        py: 0.5,
                        '&.Mui-selected': {},
                        '&:not(:first-of-type)': {
                          marginLeft: '0 !important',
                        }
                      }
                    }}
                  >
                    <ToggleButton 
                      value="good" 
                      sx={{ 
                        textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', width: 145, whiteSpace: 'nowrap',
                        color: feedbackState.recommendationResult === 'good' ? '#1d8ef0' : '#64748b',
                        borderColor: feedbackState.recommendationResult === 'good' ? '#1d8ef0 !important' : '#e2e8f0 !important',
                        bgcolor: feedbackState.recommendationResult === 'good' ? alpha('#1d8ef0', 0.08) : 'transparent',
                        '&.Mui-selected': {
                          color: '#1d8ef0',
                          bgcolor: alpha('#1d8ef0', 0.08),
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: alpha('#1d8ef0', 0.12),
                        }
                      }}
                    >
                      {feedbackState.recommendationResult === 'good' ? <CheckCircleRoundedIcon sx={{ fontSize: 18, mr: 0.75, color: '#1d8ef0' }} /> : null}
                      <ThumbUpOutlinedIcon sx={{ fontSize: 15, mr: 0.75, mb: 0.25 }} /> Good
                    </ToggleButton>
                    <ToggleButton 
                      value="needs_work" 
                      sx={{ 
                        textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', width: 145, whiteSpace: 'nowrap',
                        color: '#f97316',
                        borderColor: feedbackState.recommendationResult === 'needs_work' ? '#f97316 !important' : alpha('#f97316', 0.4) + ' !important',
                        bgcolor: feedbackState.recommendationResult === 'needs_work' ? alpha('#f97316', 0.08) : 'transparent',
                        '&.Mui-selected': {
                          color: '#f97316',
                          bgcolor: alpha('#f97316', 0.08),
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: alpha('#f97316', 0.12),
                        }
                      }}
                    >
                      {feedbackState.recommendationResult === 'needs_work' ? <CheckCircleRoundedIcon sx={{ fontSize: 18, mr: 0.75, color: '#f97316' }} /> : null}
                      <WarningAmberRoundedIcon sx={{ fontSize: 15, mr: 0.75, mb: 0.25 }} /> Needs work
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Stack>

              {feedbackState.recommendationResult === 'good' && (
                    <Box sx={{ bgcolor: '#f0fdf4', p: 1.1, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleRoundedIcon sx={{ fontSize: 22, color: '#22c55e' }} />
                  <Typography variant="body2" sx={{ fontWeight: 400, color: '#16a34a',  fontSize:"12px", }}>Thanks! Your feedback for recommendation has been recorded.</Typography>
                </Box>
              )}

            {feedbackState.recommendationResult === 'needs_work' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
                  Recommendation Details <span style={{color: '#ef4444'}}>*</span>
                </Typography>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="Describe the issue with the recommendation..."
                    value={feedbackState.recommendationText}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFeedbackState(prev => ({ ...prev, recommendationText: e.target.value }));
                      }
                    }}
                    disabled={feedbackSubmitting}
                    error={!feedbackState.recommendationText.trim()}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                        '&.Mui-focused fieldset': { borderColor: '#1d8ef0', borderWidth: '1px' },
                        '&.Mui-error fieldset': { borderColor: '#ef4444' },
                        pb: 3
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ position: 'absolute', bottom: 6, right: 10, color: '#94a3b8', fontSize: '0.7rem' }}>
                    {feedbackState.recommendationText.length} / 500
                  </Typography>
                </Box>
                {!feedbackState.recommendationText.trim() ? (
                  <Typography variant="caption" sx={{ color: '#ef4444', mt: 1, fontSize: "12px", display: 'block' }}>
                    Recommendation details are required
                  </Typography>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1, color: '#64748b' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption" style={{fontSize:"12px"}}>
                      Provide specific details so we can improve better.
                    </Typography>
                  </Stack>
                )}
              </Box>
            )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 2, borderTop: '1px solid #f1f5f9' }}>
          <Button 
            onClick={() => {
              setFeedbackState({ open: false, evidenceText: '', recommendationText: '', evidenceResult: '', recommendationResult: '' });
            }} 
            variant="outlined"
            // size="small"
            disabled={feedbackSubmitting}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '6px', color: '#1e293b', borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }, px: 2, py: 0.5, fontSize: '0.8125rem' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              setFeedbackSubmitting(true);
              try {
                await submitFeedbackApi({ 
                  evidenceText: feedbackState.evidenceText,
                  recommendationText: feedbackState.recommendationText,
                  evidenceResult: feedbackState.evidenceResult,
                  recommendationResult: feedbackState.recommendationResult
                });
                toast.success('Feedback submitted successfully!');
                setFeedbackState({ open: false, evidenceText: '', recommendationText: '', evidenceResult: '', recommendationResult: '' });
              } catch (error) {
                toast.error('Failed to submit feedback.');
              } finally {
                setFeedbackSubmitting(false);
              }
            }} 
            variant="contained"
            // size="small"
            disabled={
              (!feedbackState.evidenceResult && !feedbackState.recommendationResult) ||
              (feedbackState.evidenceResult === 'needs_work' && !feedbackState.evidenceText.trim()) || 
              (feedbackState.recommendationResult === 'needs_work' && !feedbackState.recommendationText.trim()) || 
              feedbackSubmitting
            }
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', borderRadius: '6px', bgcolor: '#1d8ef0', '&:hover': { bgcolor: '#177cd2', boxShadow: 'none' }, px: 2, py: 0.5, fontSize: '0.8125rem' }}
            startIcon={feedbackSubmitting ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {feedbackSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {reqId ? renderNewFooter() : null}
    </Box>
  );
};

export default HCPDCR;
